/* Source: /home/ubuntu/libredraw/frontend/editor/RightPanel.cl.jac */
import {__jacJsx, __jacSpawn} from "@jac/runtime";
import { SlidersHorizontal, Layers, MessageSquare, Sparkles, PanelRightClose, Sun, Moon, Share2, Download, Send, Trash2, Check, Plus, Square, Circle, Diamond, Cylinder, Triangle, Hexagon, Type, StickyNote, Spline, ChevronDown, ChevronRight, Folder, AlignStartVertical, AlignCenterVertical, AlignEndVertical, AlignStartHorizontal, AlignCenterHorizontal, AlignEndHorizontal, AlignHorizontalDistributeCenter, AlignVerticalDistributeCenter, BringToFront, SendToBack, MoveUp, MoveDown, Lock, LockOpen, Eye, EyeOff } from "lucide-react";
import { cn } from "../../lib/utils.js";
import { FONTS, fontStack, STROKE_SWATCHES } from "./canvas/geometry.ts";
import { useState } from "@jac/runtime";
const _jac = {
  int: {
    bit_length(n) {
      if (n === 0) return 0;
      return Math.floor(Math.log2(Math.abs(n))) + 1;
    },
    bit_count(n) {
      let x = Math.abs(n);
      let count = 0;
      while (x) { count += x & 1; x >>= 1; }
      return count;
    },
    to_bytes(n, length, byteorder) {
      const bytes = [];
      let val = n < 0 ? (1 << (length * 8)) + n : n;
      for (let i = 0; i < length; i++) {
        bytes.push(val & 0xff);
        val >>= 8;
      }
      if (byteorder === "big") bytes.reverse();
      return bytes;
    },
    from_bytes(bytes, byteorder) {
      const b = byteorder === "big" ? bytes : [...bytes].reverse();
      let result = 0;
      for (const byte of b) result = (result << 8) | byte;
      return result;
    },
    mod(a, b) { return ((a % b) + b) % b; }
  },

  float: {
    as_integer_ratio(f) {
      if (!isFinite(f)) throw new _jac.exc.ValueError("cannot convert " + f + " to integer ratio");
      if (f === 0) return [0, 1];
      let num = f, den = 1;
      while (num !== Math.floor(num)) { num *= 2; den *= 2; }
      const g = _jac.float._gcd(Math.abs(num), den);
      return [num / g, den / g];
    },
    _gcd(a, b) { while (b) { [a, b] = [b, a % b]; } return a; },
    hex(f) {
      return f.toString(16);
    },
    fromhex(s) {
      return parseFloat(s);
    },
    mod(a, b) { return ((a % b) + b) % b; }
  },

  complex: {
    conjugate(c) { return {re: c.re, im: -c.im}; },
    add(a, b) { return {re: a.re + b.re, im: a.im + b.im}; },
    sub(a, b) { return {re: a.re - b.re, im: a.im - b.im}; },
    mul(a, b) { return {re: a.re * b.re - a.im * b.im, im: a.re * b.im + a.im * b.re}; },
    truediv(a, b) {
      const d = b.re * b.re + b.im * b.im;
      return {re: (a.re * b.re + a.im * b.im) / d, im: (a.im * b.re - a.re * b.im) / d};
    },
    pow(a, b) {
      if (typeof b === "number") b = {re: b, im: 0};
      if (typeof a === "number") a = {re: a, im: 0};
      if (b.im === 0 && Number.isInteger(b.re)) {
        let result = {re: 1, im: 0};
        for (let i = 0; i < Math.abs(b.re); i++) result = _jac.complex.mul(result, a);
        return b.re < 0 ? _jac.complex.truediv({re: 1, im: 0}, result) : result;
      }
      const r = Math.sqrt(a.re * a.re + a.im * a.im);
      const theta = Math.atan2(a.im, a.re);
      const lnr = Math.log(r);
      const newR = Math.exp(lnr * b.re - theta * b.im);
      const newTheta = lnr * b.im + theta * b.re;
      return {re: newR * Math.cos(newTheta), im: newR * Math.sin(newTheta)};
    },
    eq(a, b) {
      if (typeof b === "number") b = {re: b, im: 0};
      if (typeof a === "number") a = {re: a, im: 0};
      return a.re === b.re && a.im === b.im;
    },
    neg(a) { return {re: -a.re, im: -a.im}; },
    pos(a) { return {re: +a.re, im: +a.im}; }
  },

  str: {
    capitalize(s) {
      if (s.length === 0) return s;
      return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
    },
    title(s) {
      return s.replace(/\b\w/g, c => c.toUpperCase());
    },
    swapcase(s) {
      return [...s].map(c => c === c.toUpperCase() ? c.toLowerCase() : c.toUpperCase()).join("");
    },
    count(s, sub, start, end) {
      const t = s.slice(start || 0, end);
      if (sub === "") return t.length + 1;
      let count = 0, pos = 0;
      while ((pos = t.indexOf(sub, pos)) !== -1) { count++; pos += sub.length; }
      return count;
    },
    find(s, sub, start, end) {
      const t = s.slice(start || 0, end);
      const idx = t.indexOf(sub);
      return idx === -1 ? -1 : idx + (start || 0);
    },
    rfind(s, sub, start, end) {
      const t = s.slice(start || 0, end);
      const idx = t.lastIndexOf(sub);
      return idx === -1 ? -1 : idx + (start || 0);
    },
    index(s, sub, start, end) {
      const idx = _jac.str.find(s, sub, start, end);
      if (idx === -1) throw new _jac.exc.ValueError("substring not found");
      return idx;
    },
    rindex(s, sub, start, end) {
      const idx = _jac.str.rfind(s, sub, start, end);
      if (idx === -1) throw new _jac.exc.ValueError("substring not found");
      return idx;
    },
    startswith(s, prefix, start, end) {
      return s.slice(start || 0, end).startsWith(prefix);
    },
    endswith(s, suffix, start, end) {
      return s.slice(start || 0, end).endsWith(suffix);
    },
    replace(s, old, new_, count) {
      if (count === undefined) return s.split(old).join(new_);
      let result = s, n = 0;
      while (n < count) {
        const idx = result.indexOf(old);
        if (idx === -1) break;
        result = result.slice(0, idx) + new_ + result.slice(idx + old.length);
        n++;
      }
      return result;
    },
    strip(s, chars) {
      if (!chars) return s.trim();
      let start = 0, end = s.length;
      while (start < end && chars.indexOf(s[start]) !== -1) start++;
      while (end > start && chars.indexOf(s[end - 1]) !== -1) end--;
      return s.slice(start, end);
    },
    lstrip(s, chars) {
      if (!chars) return s.trimStart();
      let start = 0;
      while (start < s.length && chars.indexOf(s[start]) !== -1) start++;
      return s.slice(start);
    },
    rstrip(s, chars) {
      if (!chars) return s.trimEnd();
      let end = s.length;
      while (end > 0 && chars.indexOf(s[end - 1]) !== -1) end--;
      return s.slice(0, end);
    },
    removeprefix(s, prefix) {
      return s.startsWith(prefix) ? s.slice(prefix.length) : s;
    },
    removesuffix(s, suffix) {
      return (suffix && s.endsWith(suffix)) ? s.slice(0, -suffix.length) : s;
    },
    split(s, sep, maxsplit) {
      if (sep === undefined || sep === null) {
        const parts = s.trim().split(/\s+/);
        if (maxsplit === undefined) return parts;
        if (maxsplit <= 0) return [s];
        return [...parts.slice(0, maxsplit), parts.slice(maxsplit).join(" ")].filter(x => x !== undefined);
      }
      if (maxsplit === undefined) return s.split(sep);
      const parts = s.split(sep);
      if (parts.length <= maxsplit + 1) return parts;
      return [...parts.slice(0, maxsplit), parts.slice(maxsplit).join(sep)];
    },
    rsplit(s, sep, maxsplit) {
      if (maxsplit === undefined) return _jac.str.split(s, sep);
      const parts = s.split(sep);
      if (parts.length <= maxsplit + 1) return parts;
      const keep = parts.length - maxsplit;
      return [parts.slice(0, keep).join(sep), ...parts.slice(keep)];
    },
    splitlines(s, keepends) {
      const lines = s.split(/(\r\n|\r|\n)/);
      const result = [];
      for (let i = 0; i < lines.length; i += 2) {
        const line = lines[i];
        const sep = lines[i + 1] || "";
        if (line || sep) result.push(keepends ? line + sep : line);
      }
      return result;
    },
    partition(s, sep) {
      const idx = s.indexOf(sep);
      if (idx === -1) return [s, "", ""];
      return [s.slice(0, idx), sep, s.slice(idx + sep.length)];
    },
    rpartition(s, sep) {
      const idx = s.lastIndexOf(sep);
      if (idx === -1) return ["", "", s];
      return [s.slice(0, idx), sep, s.slice(idx + sep.length)];
    },
    format(s, ...args) {
      let i = 0;
      return s.replace(/\{(\w*)\}/g, (match, key) => {
        if (key === "") return args[i++];
        if (!isNaN(key)) return args[parseInt(key)];
        return args[0] && typeof args[0] === "object" ? args[0][key] : match;
      });
    },
    format_map(s, mapping) {
      return s.replace(/\{(\w+)\}/g, (match, key) => key in mapping ? mapping[key] : match);
    },
    center(s, width, fillchar) {
      const fc = fillchar || " ";
      if (s.length >= width) return s;
      const total = width - s.length;
      const left = Math.floor(total / 2);
      return fc.repeat(left) + s + fc.repeat(total - left);
    },
    zfill(s, width) {
      if (s.length >= width) return s;
      const sign = (s[0] === "+" || s[0] === "-") ? s[0] : "";
      const body = sign ? s.slice(1) : s;
      return sign + "0".repeat(width - s.length) + body;
    },
    expandtabs(s, tabsize) {
      const ts = tabsize === undefined ? 8 : tabsize;
      let result = "", col = 0;
      for (const c of s) {
        if (c === "\t") { const sp = ts - (col % ts); result += " ".repeat(sp); col += sp; }
        else if (c === "\n" || c === "\r") { result += c; col = 0; }
        else { result += c; col++; }
      }
      return result;
    },
    isnumeric(s) {
      if (s.length === 0) return false;
      return [...s].every(c => /[\d\u00B2\u00B3\u00B9\u00BC-\u00BE\u2150-\u218F]/.test(c));
    },
    istitle(s) {
      return s === _jac.str.title(s) && /[A-Za-z]/.test(s);
    },
    translate(s, table) {
      return [...s].map(c => {
        const code = c.charCodeAt(0);
        if (code in table) {
          const v = table[code];
          if (v === null) return "";
          if (typeof v === "number") return String.fromCharCode(v);
          return v;
        }
        return c;
      }).join("");
    },
    maketrans(...args) {
      const table = {};
      if (args.length === 1) {
        const mapping = args[0];
        for (const [k, v] of Object.entries(mapping)) {
          table[typeof k === "string" ? k.charCodeAt(0) : k] = v;
        }
      } else if (args.length >= 2) {
        const from_ = args[0], to_ = args[1];
        for (let i = 0; i < from_.length; i++) {
          table[from_.charCodeAt(i)] = to_.charCodeAt(i);
        }
        if (args[2]) {
          for (const c of args[2]) table[c.charCodeAt(0)] = null;
        }
      }
      return table;
    },
    mod(s, args) {
      let i = 0;
      const a = Array.isArray(args) ? args : [args];
      return s.replace(/%([sd%])/g, (m, fmt) => {
        if (fmt === "%") return "%";
        return String(a[i++]);
      });
    }
  },

  // Runtime-polymorphic method dispatch, used when the codegen cannot prove
  // the static type of a method-call receiver (e.g. a `dict`-typed prop, the
  // result of `or` widening, or a comprehension over an untyped value) and so
  // cannot pick a typed emitter. The compiler emits
  // `_jac.poly.call(obj, "name", ...args)`; `call` resolves the *runtime* type
  // of `obj` and applies the matching Python operation, forwards to a
  // same-named method on a user-defined object, or raises AttributeError. The
  // per-type tables below carry the trivial native-mapped methods inline and
  // delegate the non-trivial ones to the shared `_jac.str/list/dict/set`
  // helpers, so each operation has a single implementation and no Python
  // method name can leak verbatim into the emitted JavaScript.
  poly: {
    _typename(o) {
      if (o === null || o === undefined) return "NoneType";
      if (Array.isArray(o)) return "list";
      if (o instanceof Set) return "set";
      if (o instanceof Map) return "dict";
      if (o instanceof Uint8Array) return "bytes";
      const t = typeof o;
      if (t === "string") return "str";
      if (t === "boolean") return "bool";
      if (t === "number") return Number.isInteger(o) ? "int" : "float";
      if (t === "object") return (o.constructor && o.constructor.name) || "object";
      return t;
    },
    // --- per-runtime-type Python method tables (keyed by Python name) ---
    // Trivial native-mapped ops are inline; non-trivial ones delegate to the
    // shared `_jac.<type>` helpers (which the typed emitters also use), so the
    // typed and polymorphic paths share one implementation.
    _str: {
      lower: (s) => s.toLowerCase(),
      upper: (s) => s.toUpperCase(),
      casefold: (s) => s.toLowerCase(),
      strip: (s, chars) => {
        if (chars === undefined) return s.trim();
        let i = 0, j = s.length;
        while (i < j && chars.indexOf(s[i]) !== -1) i++;
        while (j > i && chars.indexOf(s[j - 1]) !== -1) j--;
        return s.slice(i, j);
      },
      lstrip: (s, chars) => {
        if (chars === undefined) return s.replace(/^\s+/, "");
        let i = 0;
        while (i < s.length && chars.indexOf(s[i]) !== -1) i++;
        return s.slice(i);
      },
      rstrip: (s, chars) => {
        if (chars === undefined) return s.replace(/\s+$/, "");
        let j = s.length;
        while (j > 0 && chars.indexOf(s[j - 1]) !== -1) j--;
        return s.slice(0, j);
      },
      join: (s, parts) => Array.from(parts, x => String(x)).join(s)
    },
    _list: {
      append: (a, x) => { a.push(x); return null; },
      clear: (a) => { a.length = 0; return null; },
      copy: (a) => a.slice(),
      count: (a, x) => _jac.list.count(a, x),
      extend: (a, b) => { for (const v of b) a.push(v); return null; },
      index: (a, x, start, end) => _jac.list.index(a, x, start, end),
      insert: (a, i, x) => { a.splice(i, 0, x); return null; },
      pop: (a, i) => {
        if (i === undefined) return a.pop();
        const idx = i < 0 ? a.length + i : i;
        return a.splice(idx, 1)[0];
      },
      remove: (a, x) => _jac.list.remove(a, x),
      reverse: (a) => { a.reverse(); return null; },
      sort: (a, key) => _jac.list.sort(a, key)
    },
    _dict: {
      clear: (o) => _jac.dict.clear(o),
      copy: (o) => ({ ...o }),
      // Python `dict.get`: returns the stored value when the key is present
      // (even if it is null), and the default only when the key is absent.
      // A membership check is required — `o[key] ?? default_` would wrongly
      // substitute the default for a present-but-null value.
      get: (o, key, default_) =>
        Object.prototype.hasOwnProperty.call(o, key) ? o[key] : default_,
      items: (o) => Object.entries(o),
      keys: (o) => Object.keys(o),
      pop: (o, key, default_) => _jac.dict.pop(o, key, default_),
      popitem: (o) => _jac.dict.popitem(o),
      setdefault: (o, key, default_) => _jac.dict.setdefault(o, key, default_),
      update: (o, other) => {
        if (other instanceof Map) { for (const [k, v] of other) o[k] = v; }
        else if (Array.isArray(other)) { for (const [k, v] of other) o[k] = v; }
        else if (other) { for (const k of Object.keys(other)) o[k] = other[k]; }
        return null;
      },
      values: (o) => Object.values(o)
    },
    _set: {
      add: (s, x) => { s.add(x); return null; },
      clear: (s) => { s.clear(); return null; },
      copy: (s) => new Set(s),
      difference: (a, b) => _jac.set.difference(a, b),
      difference_update: (s, o) => _jac.set.difference_update(s, o),
      discard: (s, x) => { s.delete(x); return null; },
      intersection: (a, b) => _jac.set.intersection(a, b),
      intersection_update: (s, o) => _jac.set.intersection_update(s, o),
      isdisjoint: (a, b) => _jac.set.isDisjointFrom(a, b instanceof Set ? b : new Set(b)),
      issubset: (a, b) => _jac.set.isSubsetOf(a, b instanceof Set ? b : new Set(b)),
      issuperset: (a, b) => _jac.set.isSupersetOf(a, b instanceof Set ? b : new Set(b)),
      pop: (s) => _jac.set.pop(s),
      remove: (s, x) => _jac.set.remove(s, x),
      symmetric_difference: (a, b) => _jac.set.symmetricDifference(a, b),
      symmetric_difference_update: (s, o) => _jac.set.symmetric_difference_update(s, o),
      union: (a, b) => _jac.set.union(a, b),
      update: (s, o) => _jac.set.update(s, o)
    },
    // Jac dicts compile to plain objects, but interop code may hand us a real
    // Map; mirror the dict methods over the Map API for that case.
    _map: {
      clear: (m) => { m.clear(); return null; },
      copy: (m) => new Map(m),
      get: (m, key, default_) => (m.has(key) ? m.get(key) : default_),
      items: (m) => [...m.entries()],
      keys: (m) => [...m.keys()],
      pop: (m, key, default_) => {
        if (m.has(key)) { const v = m.get(key); m.delete(key); return v; }
        if (default_ !== undefined) return default_;
        throw new _jac.exc.KeyError("KeyError: " + key);
      },
      popitem: (m) => {
        if (m.size === 0) throw new _jac.exc.KeyError("dictionary is empty");
        const key = [...m.keys()].pop();
        const val = m.get(key);
        m.delete(key);
        return [key, val];
      },
      setdefault: (m, key, default_) => {
        if (!m.has(key)) m.set(key, default_ === undefined ? null : default_);
        return m.get(key);
      },
      update: (m, other) => {
        if (other instanceof Map) { for (const [k, v] of other) m.set(k, v); }
        else if (Array.isArray(other)) { for (const [k, v] of other) m.set(k, v); }
        else if (other) { for (const k of Object.keys(other)) m.set(k, other[k]); }
        return null;
      },
      values: (m) => [...m.values()]
    },
    // Single entry point. `obj.method(...args)` on a receiver whose static
    // type is not provably primitive is compiled to
    // `_jac.poly.call(obj, "method", ...args)`.
    call(o, m, ...args) {
      // 1. Genuine JS primitives get Python-semantic dispatch: a plain object
      //    is a Python dict, an Array is a list, a Set/Map/string/number/bytes
      //    maps to its Python counterpart. These are the types whose Python
      //    method semantics differ from (or have no) native JS equivalent.
      if (typeof o === "string") {
        if (_jac.poly._str[m]) return _jac.poly._str[m](o, ...args);
        if (_jac.str[m]) return _jac.str[m](o, ...args);
      } else if (typeof o === "number") {
        const ns = Number.isInteger(o) ? _jac.int : _jac.float;
        if (ns && ns[m]) return ns[m](o, ...args);
      } else if (Array.isArray(o)) {
        if (_jac.poly._list[m]) return _jac.poly._list[m](o, ...args);
      } else if (o instanceof Set) {
        if (_jac.poly._set[m]) return _jac.poly._set[m](o, ...args);
      } else if (o instanceof Map) {
        if (_jac.poly._map[m]) return _jac.poly._map[m](o, ...args);
      } else if (o instanceof Uint8Array) {
        if (_jac.bytes[m]) return _jac.bytes[m](o, ...args);
      } else if (
        o !== null && o !== undefined && typeof o === "object"
        && (Object.getPrototypeOf(o) === Object.prototype
            || Object.getPrototypeOf(o) === null)
      ) {
        // A plain object is a Python dict literal.
        if (_jac.poly._dict[m]) return _jac.poly._dict[m](o, ...args);
      }
      // 2. Otherwise forward to a same-named method the receiver carries
      //    itself: a user-defined class instance, a host/framework object
      //    (Headers, URLSearchParams, ...), a callable with attached methods,
      //    or a Set/Map/Array method not mirrored above. This matches the
      //    direct `obj.method(...)` that a typed call would have emitted.
      if (o !== null && o !== undefined && typeof o[m] === "function") {
        return o[m](...args);
      }
      // 3. Nothing applies: raise the Python-style error instead of leaking a
      //    method name or silently returning a wrong value.
      throw new _jac.exc.AttributeError(
        "'" + _jac.poly._typename(o) + "' object has no attribute '" + m + "'");
    },
    // Backward-compatible named shims. The compiler now emits `call`, but a
    // previously-built bundle may still reference a named helper; these route
    // it through the same dispatcher (rest params preserve the arg count).
    lower(s, ...r) { return _jac.poly.call(s, "lower", ...r); },
    upper(s, ...r) { return _jac.poly.call(s, "upper", ...r); },
    strip(s, ...r) { return _jac.poly.call(s, "strip", ...r); },
    lstrip(s, ...r) { return _jac.poly.call(s, "lstrip", ...r); },
    rstrip(s, ...r) { return _jac.poly.call(s, "rstrip", ...r); },
    append(a, ...r) { return _jac.poly.call(a, "append", ...r); },
    extend(a, ...r) { return _jac.poly.call(a, "extend", ...r); },
    pop(a, ...r) { return _jac.poly.call(a, "pop", ...r); },
    get(o, ...r) { return _jac.poly.call(o, "get", ...r); },
    contains(c, x) {
      // Python-equivalent membership for a value of unknown static type.
      // string -> substring; array (list/tuple/range) -> element;
      // Set/Map -> key/element; plain object (dict) -> own key.
      if (typeof c === "string") return c.includes(x);
      if (Array.isArray(c)) return c.includes(x);
      if (c instanceof Set || c instanceof Map) return c.has(x);
      if (c !== null && typeof c === "object") {
        return Object.prototype.hasOwnProperty.call(c, x);
      }
      return false;
    },
    getitem(c, i) {
      // Subscript read for a value of unknown static type. This is a faithful
      // superset of a raw `c[i]`: it only adds Python's negative indexing for
      // sequences (string/array) and otherwise preserves JS semantics exactly
      // (mapping/object access, out-of-range -> `undefined`, no new throws).
      if (typeof i === "number" && i < 0
          && (typeof c === "string" || Array.isArray(c))) {
        return c[c.length + i];
      }
      if (c instanceof Map) return c.get(i);
      return c[i];
    },
    setitem(c, i, v) {
      // Subscript assignment for a value of unknown/list static type. A
      // negative index writes the array element counted from the end (a raw
      // JS `c[-1] = v` would instead create a bogus "-1" property); every
      // other case behaves like a plain `c[i] = v`.
      if (typeof i === "number" && i < 0 && Array.isArray(c)) {
        c[c.length + i] = v;
        return v;
      }
      if (c instanceof Map) { c.set(i, v); return v; }
      c[i] = v;
      return v;
    },
    and_(a, bf) {
      // Python `a and b`: returns `a` when it is falsy, else evaluates and
      // returns `b`. `bf` is a thunk so the right operand keeps short-circuit
      // (lazy) evaluation, and `a` is evaluated exactly once by the caller.
      return _jac.builtin.bool(a) ? bf() : a;
    },
    or_(a, bf) {
      // Python `a or b`: returns `a` when it is truthy, else `b`.
      return _jac.builtin.bool(a) ? a : bf();
    }
  },

  list: {
    remove(arr, val) {
      const i = arr.indexOf(val);
      if (i === -1) throw new _jac.exc.ValueError("list.remove(x): x not in list");
      arr.splice(i, 1);
    },
    index(arr, val, start, end) {
      const s = start || 0;
      const e = end === undefined ? arr.length : end;
      for (let i = s; i < e; i++) {
        if (arr[i] === val) return i;
      }
      throw new _jac.exc.ValueError(val + " is not in list");
    },
    count(arr, val) {
      let c = 0;
      for (const item of arr) { if (item === val) c++; }
      return c;
    },
    sort(arr, key) {
      if (key) {
        arr.sort((a, b) => {
          const ka = key(a), kb = key(b);
          return ka < kb ? -1 : ka > kb ? 1 : 0;
        });
      } else {
        arr.sort((a, b) => a < b ? -1 : a > b ? 1 : 0);
      }
    },
    repeat(arr, n) {
      const result = [];
      for (let i = 0; i < n; i++) result.push(...arr);
      return result;
    },
    eq(a, b) { return JSON.stringify(a) === JSON.stringify(b); },
    lt(a, b) {
      for (let i = 0; i < Math.min(a.length, b.length); i++) {
        if (a[i] < b[i]) return true;
        if (a[i] > b[i]) return false;
      }
      return a.length < b.length;
    },
    gt(a, b) { return _jac.list.lt(b, a); },
    le(a, b) { return _jac.list.eq(a, b) || _jac.list.lt(a, b); },
    ge(a, b) { return _jac.list.eq(a, b) || _jac.list.gt(a, b); },
    imul(arr, n) {
      const orig = [...arr];
      arr.length = 0;
      for (let i = 0; i < n; i++) arr.push(...orig);
      return arr;
    }
  },

  dict: {
    pop(d, key, default_) {
      if (key in d) { const v = d[key]; delete d[key]; return v; }
      if (default_ !== undefined) return default_;
      throw new _jac.exc.KeyError("KeyError: " + key);
    },
    popitem(d) {
      const keys = Object.keys(d);
      if (keys.length === 0) throw new _jac.exc.KeyError("dictionary is empty");
      const key = keys[keys.length - 1];
      const val = d[key];
      delete d[key];
      return [key, val];
    },
    setdefault(d, key, default_) {
      if (!(key in d)) d[key] = default_ === undefined ? null : default_;
      return d[key];
    },
    clear(d) {
      for (const key of Object.keys(d)) delete d[key];
    },
    fromkeys(keys, value) {
      const d = {};
      const v = value === undefined ? null : value;
      for (const k of keys) d[k] = v;
      return d;
    },
    eq(a, b) {
      const ka = Object.keys(a), kb = Object.keys(b);
      if (ka.length !== kb.length) return false;
      for (const k of ka) {
        if (!(k in b)) return false;
        const va = a[k], vb = b[k];
        if (va !== vb) {
          if (typeof va === "object" && typeof vb === "object" && va !== null && vb !== null) {
            if (JSON.stringify(va) !== JSON.stringify(vb)) return false;
          } else return false;
        }
      }
      return true;
    }
  },

  set: {
    remove(s, val) {
      if (!s.has(val)) throw new _jac.exc.KeyError("KeyError: " + val);
      s.delete(val);
    },
    pop(s) {
      if (s.size === 0) throw new _jac.exc.KeyError("pop from an empty set");
      const val = s.values().next().value;
      s.delete(val);
      return val;
    },
    update(s, other) { for (const v of other) s.add(v); },
    intersection_update(s, other) {
      const otherSet = other instanceof Set ? other : new Set(other);
      for (const v of s) { if (!otherSet.has(v)) s.delete(v); }
    },
    difference_update(s, other) {
      for (const v of other) s.delete(v);
    },
    symmetric_difference_update(s, other) {
      for (const v of other) { if (s.has(v)) s.delete(v); else s.add(v); }
    },
    union(a, b) {
      const r = new Set(a);
      for (const v of b) r.add(v);
      return r;
    },
    intersection(a, b) {
      const bs = b instanceof Set ? b : new Set(b);
      const r = new Set();
      for (const v of a) { if (bs.has(v)) r.add(v); }
      return r;
    },
    difference(a, b) {
      const bs = b instanceof Set ? b : new Set(b);
      const r = new Set();
      for (const v of a) { if (!bs.has(v)) r.add(v); }
      return r;
    },
    symmetricDifference(a, b) {
      const r = new Set(a);
      for (const v of b) { if (r.has(v)) r.delete(v); else r.add(v); }
      return r;
    },
    isSubsetOf(a, b) {
      if (a.size > b.size) return false;
      for (const v of a) { if (!b.has(v)) return false; }
      return true;
    },
    isSupersetOf(a, b) { return _jac.set.isSubsetOf(b, a); },
    isDisjointFrom(a, b) {
      for (const v of a) { if (b.has(v)) return false; }
      return true;
    },
    is_proper_subset(a, b) {
      if (a.size >= b.size) return false;
      for (const v of a) { if (!b.has(v)) return false; }
      return true;
    },
    is_proper_superset(a, b) { return _jac.set.is_proper_subset(b, a); },
    eq(a, b) {
      if (a.size !== b.size) return false;
      for (const v of a) { if (!b.has(v)) return false; }
      return true;
    }
  },

  bytes: {
    hex(b) {
      return Array.from(b).map(x => x.toString(16).padStart(2, "0")).join("");
    },
    fromhex(s) {
      const arr = [];
      const clean = s.replace(/\s/g, "");
      for (let i = 0; i < clean.length; i += 2) arr.push(parseInt(clean.substr(i, 2), 16));
      return new Uint8Array(arr);
    },
    _indexOf(b, sub, start, end) {
      const s = start || 0;
      const e = end === undefined ? b.length : end;
      const sl = sub.length;
      if (sl === 0) return s;
      for (let i = s; i <= e - sl; i++) {
        let m = true;
        for (let j = 0; j < sl; j++) { if (b[i + j] !== sub[j]) { m = false; break; } }
        if (m) return i;
      }
      return -1;
    },
    _lastIndexOf(b, sub, start, end) {
      const s = start || 0;
      const e = end === undefined ? b.length : end;
      const sl = sub.length;
      if (sl === 0) return e;
      for (let i = e - sl; i >= s; i--) {
        let m = true;
        for (let j = 0; j < sl; j++) { if (b[i + j] !== sub[j]) { m = false; break; } }
        if (m) return i;
      }
      return -1;
    },
    count(b, sub) {
      let c = 0, p = 0;
      while (p <= b.length - sub.length) {
        const idx = _jac.bytes._indexOf(b, sub, p);
        if (idx === -1) break;
        c++; p = idx + sub.length;
      }
      return c;
    },
    find(b, sub, start, end) { return _jac.bytes._indexOf(b, sub, start, end); },
    rfind(b, sub, start, end) { return _jac.bytes._lastIndexOf(b, sub, start, end); },
    index(b, sub, start, end) {
      const idx = _jac.bytes._indexOf(b, sub, start, end);
      if (idx === -1) throw new _jac.exc.ValueError("substring not found");
      return idx;
    },
    rindex(b, sub, start, end) {
      const idx = _jac.bytes._lastIndexOf(b, sub, start, end);
      if (idx === -1) throw new _jac.exc.ValueError("substring not found");
      return idx;
    },
    startswith(b, prefix, start, end) {
      const s = start || 0;
      if (prefix.length > b.length - s) return false;
      for (let i = 0; i < prefix.length; i++) { if (b[s + i] !== prefix[i]) return false; }
      return true;
    },
    endswith(b, suffix, start, end) {
      const e = end === undefined ? b.length : end;
      if (suffix.length > e) return false;
      const off = e - suffix.length;
      for (let i = 0; i < suffix.length; i++) { if (b[off + i] !== suffix[i]) return false; }
      return true;
    },
    replace(b, old_, new_, count) {
      const r = [];
      let pos = 0, n = 0;
      const max = count === undefined ? Infinity : count;
      while (pos <= b.length) {
        if (n < max) {
          const idx = _jac.bytes._indexOf(b, old_, pos);
          if (idx !== -1) {
            for (let i = pos; i < idx; i++) r.push(b[i]);
            for (let i = 0; i < new_.length; i++) r.push(new_[i]);
            pos = idx + old_.length; n++;
            continue;
          }
        }
        for (let i = pos; i < b.length; i++) r.push(b[i]);
        break;
      }
      return new Uint8Array(r);
    },
    _isWS(c) { return c === 32 || c === 9 || c === 10 || c === 13 || c === 11 || c === 12; },
    strip(b, chars) {
      let s = 0, e = b.length;
      if (chars) { const cs = new Set(chars); while (s < e && cs.has(b[s])) s++; while (e > s && cs.has(b[e-1])) e--; }
      else { while (s < e && _jac.bytes._isWS(b[s])) s++; while (e > s && _jac.bytes._isWS(b[e-1])) e--; }
      return b.slice(s, e);
    },
    lstrip(b, chars) {
      let s = 0;
      if (chars) { const cs = new Set(chars); while (s < b.length && cs.has(b[s])) s++; }
      else { while (s < b.length && _jac.bytes._isWS(b[s])) s++; }
      return b.slice(s);
    },
    rstrip(b, chars) {
      let e = b.length;
      if (chars) { const cs = new Set(chars); while (e > 0 && cs.has(b[e-1])) e--; }
      else { while (e > 0 && _jac.bytes._isWS(b[e-1])) e--; }
      return b.slice(0, e);
    },
    removeprefix(b, prefix) {
      if (_jac.bytes.startswith(b, prefix)) return b.slice(prefix.length);
      return b.slice();
    },
    removesuffix(b, suffix) {
      if (suffix.length > 0 && _jac.bytes.endswith(b, suffix)) return b.slice(0, b.length - suffix.length);
      return b.slice();
    },
    split(b, sep, maxsplit) {
      const parts = [];
      let pos = 0, n = 0;
      const max = maxsplit === undefined ? Infinity : maxsplit;
      while (pos <= b.length) {
        if (n >= max) { parts.push(b.slice(pos)); return parts; }
        const idx = _jac.bytes._indexOf(b, sep, pos);
        if (idx === -1) { parts.push(b.slice(pos)); return parts; }
        parts.push(b.slice(pos, idx));
        pos = idx + sep.length; n++;
      }
      if (pos <= b.length) parts.push(b.slice(pos));
      return parts;
    },
    rsplit(b, sep, maxsplit) {
      if (maxsplit === undefined) return _jac.bytes.split(b, sep);
      const parts = [];
      let end = b.length, n = 0;
      while (end >= 0 && n < maxsplit) {
        const idx = _jac.bytes._lastIndexOf(b, sep, 0, end);
        if (idx === -1) break;
        parts.unshift(b.slice(idx + sep.length, end));
        end = idx; n++;
      }
      parts.unshift(b.slice(0, end));
      return parts;
    },
    splitlines(b, keepends) {
      const result = [];
      let start = 0;
      for (let i = 0; i < b.length; i++) {
        if (b[i] === 10 || b[i] === 13) {
          let eol = i + 1;
          if (b[i] === 13 && i + 1 < b.length && b[i + 1] === 10) eol++;
          result.push(keepends ? b.slice(start, eol) : b.slice(start, i));
          start = eol;
          if (eol > i + 1) i++;
        }
      }
      if (start < b.length) result.push(b.slice(start));
      return result;
    },
    join(sep, parts) {
      if (parts.length === 0) return new Uint8Array(0);
      const r = [];
      for (let i = 0; i < parts.length; i++) {
        if (i > 0) for (const x of sep) r.push(x);
        for (const x of parts[i]) r.push(x);
      }
      return new Uint8Array(r);
    },
    partition(b, sep) {
      const idx = _jac.bytes._indexOf(b, sep);
      if (idx === -1) return [b.slice(), new Uint8Array(0), new Uint8Array(0)];
      return [b.slice(0, idx), sep.slice(), b.slice(idx + sep.length)];
    },
    rpartition(b, sep) {
      const idx = _jac.bytes._lastIndexOf(b, sep);
      if (idx === -1) return [new Uint8Array(0), new Uint8Array(0), b.slice()];
      return [b.slice(0, idx), sep.slice(), b.slice(idx + sep.length)];
    },
    _isUp(c) { return c >= 65 && c <= 90; },
    _isLo(c) { return c >= 97 && c <= 122; },
    _isAl(c) { return (c >= 65 && c <= 90) || (c >= 97 && c <= 122); },
    _isDig(c) { return c >= 48 && c <= 57; },
    capitalize(b) {
      if (b.length === 0) return new Uint8Array(0);
      const r = new Uint8Array(b.length);
      r[0] = _jac.bytes._isLo(b[0]) ? b[0] - 32 : b[0];
      for (let i = 1; i < b.length; i++) r[i] = _jac.bytes._isUp(b[i]) ? b[i] + 32 : b[i];
      return r;
    },
    lower(b) {
      const r = new Uint8Array(b.length);
      for (let i = 0; i < b.length; i++) r[i] = _jac.bytes._isUp(b[i]) ? b[i] + 32 : b[i];
      return r;
    },
    upper(b) {
      const r = new Uint8Array(b.length);
      for (let i = 0; i < b.length; i++) r[i] = _jac.bytes._isLo(b[i]) ? b[i] - 32 : b[i];
      return r;
    },
    title(b) {
      const r = new Uint8Array(b.length);
      let prev = false;
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isAl(b[i])) {
          r[i] = prev ? (_jac.bytes._isUp(b[i]) ? b[i] + 32 : b[i]) : (_jac.bytes._isLo(b[i]) ? b[i] - 32 : b[i]);
          prev = true;
        } else { r[i] = b[i]; prev = false; }
      }
      return r;
    },
    swapcase(b) {
      const r = new Uint8Array(b.length);
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isUp(b[i])) r[i] = b[i] + 32;
        else if (_jac.bytes._isLo(b[i])) r[i] = b[i] - 32;
        else r[i] = b[i];
      }
      return r;
    },
    center(b, width, fb) {
      const f = fb || 32;
      if (b.length >= width) return b.slice();
      const total = width - b.length;
      const left = Math.floor(total / 2);
      const r = new Uint8Array(width);
      r.fill(f, 0, left); r.set(b, left); r.fill(f, left + b.length);
      return r;
    },
    ljust(b, width, fb) {
      const f = fb || 32;
      if (b.length >= width) return b.slice();
      const r = new Uint8Array(width);
      r.set(b); r.fill(f, b.length);
      return r;
    },
    rjust(b, width, fb) {
      const f = fb || 32;
      if (b.length >= width) return b.slice();
      const r = new Uint8Array(width);
      const off = width - b.length;
      r.fill(f, 0, off); r.set(b, off);
      return r;
    },
    zfill(b, width) {
      if (b.length >= width) return b.slice();
      const r = new Uint8Array(width);
      let off = 0;
      if (b.length > 0 && (b[0] === 43 || b[0] === 45)) { r[0] = b[0]; off = 1; }
      const pad = width - b.length;
      r.fill(48, off, off + pad);
      for (let i = off; i < b.length; i++) r[pad + i] = b[i];
      return r;
    },
    expandtabs(b, tabsize) {
      const ts = tabsize === undefined ? 8 : tabsize;
      const r = [];
      let col = 0;
      for (let i = 0; i < b.length; i++) {
        if (b[i] === 9) { const sp = ts - (col % ts); for (let j = 0; j < sp; j++) r.push(32); col += sp; }
        else if (b[i] === 10 || b[i] === 13) { r.push(b[i]); col = 0; }
        else { r.push(b[i]); col++; }
      }
      return new Uint8Array(r);
    },
    translate(b, table, del_) {
      const r = [];
      const ds = del_ ? new Set(del_) : null;
      for (let i = 0; i < b.length; i++) {
        if (ds && ds.has(b[i])) continue;
        r.push(table && table[b[i]] !== undefined ? table[b[i]] : b[i]);
      }
      return new Uint8Array(r);
    },
    maketrans(from_, to_) {
      const t = new Uint8Array(256);
      for (let i = 0; i < 256; i++) t[i] = i;
      for (let i = 0; i < from_.length; i++) t[from_[i]] = to_[i];
      return t;
    },
    isalnum(b) {
      if (b.length === 0) return false;
      for (let i = 0; i < b.length; i++) if (!_jac.bytes._isAl(b[i]) && !_jac.bytes._isDig(b[i])) return false;
      return true;
    },
    isalpha(b) {
      if (b.length === 0) return false;
      for (let i = 0; i < b.length; i++) if (!_jac.bytes._isAl(b[i])) return false;
      return true;
    },
    isascii(b) {
      for (let i = 0; i < b.length; i++) if (b[i] > 127) return false;
      return true;
    },
    isdigit(b) {
      if (b.length === 0) return false;
      for (let i = 0; i < b.length; i++) if (!_jac.bytes._isDig(b[i])) return false;
      return true;
    },
    islower(b) {
      let has = false;
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isUp(b[i])) return false;
        if (_jac.bytes._isLo(b[i])) has = true;
      }
      return has;
    },
    isupper(b) {
      let has = false;
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isLo(b[i])) return false;
        if (_jac.bytes._isUp(b[i])) has = true;
      }
      return has;
    },
    isspace(b) {
      if (b.length === 0) return false;
      for (let i = 0; i < b.length; i++) if (!_jac.bytes._isWS(b[i])) return false;
      return true;
    },
    istitle(b) {
      let prev = false, has = false;
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isUp(b[i])) { if (prev) return false; prev = true; has = true; }
        else if (_jac.bytes._isLo(b[i])) { if (!prev) return false; prev = true; has = true; }
        else prev = false;
      }
      return has;
    },
    add(a, b) {
      const r = new Uint8Array(a.length + b.length);
      r.set(a); r.set(b, a.length);
      return r;
    },
    mul(b, n) {
      const r = new Uint8Array(b.length * n);
      for (let i = 0; i < n; i++) r.set(b, i * b.length);
      return r;
    },
    mod(b, args) {
      const s = new TextDecoder().decode(b);
      return new TextEncoder().encode(_jac.str.mod(s, args));
    },
    eq(a, b) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
      return true;
    },
    lt(a, b) {
      for (let i = 0; i < Math.min(a.length, b.length); i++) {
        if (a[i] < b[i]) return true;
        if (a[i] > b[i]) return false;
      }
      return a.length < b.length;
    },
    gt(a, b) { return _jac.bytes.lt(b, a); },
    le(a, b) { return _jac.bytes.eq(a, b) || _jac.bytes.lt(a, b); },
    ge(a, b) { return _jac.bytes.eq(a, b) || _jac.bytes.gt(a, b); },
    contains(b, sub) { return _jac.bytes._indexOf(b, sub) !== -1; }
  },

  tuple: {
    count(arr, val) { return _jac.list.count(arr, val); },
    index(arr, val, start, end) { return _jac.list.index(arr, val, start, end); },
    repeat(arr, n) { return _jac.list.repeat(arr, n); },
    eq(a, b) { return _jac.list.eq(a, b); },
    lt(a, b) { return _jac.list.lt(a, b); },
    gt(a, b) { return _jac.list.gt(a, b); },
    le(a, b) { return _jac.list.le(a, b); },
    ge(a, b) { return _jac.list.ge(a, b); }
  },

  range: {
    count(r, val) {
      if (typeof r[Symbol.iterator] === "function") {
        let c = 0;
        for (const v of r) { if (v === val) c++; }
        return c;
      }
      return 0;
    },
    index(r, val) {
      let i = 0;
      for (const v of r) { if (v === val) return i; i++; }
      throw new _jac.exc.ValueError(val + " is not in range");
    },
    eq(a, b) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) { if (a[i] !== b[i]) return false; }
      return true;
    },
    contains(r, val) {
      for (const v of r) { if (v === val) return true; }
      return false;
    }
  },

  builtin: {
    round(n, ndigits) {
      if (ndigits === undefined) return Math.round(n);
      const f = Math.pow(10, ndigits);
      return Math.round(n * f) / f;
    },
    sum(iterable, start) {
      let total = start || 0;
      for (const v of iterable) total += v;
      return total;
    },
    sorted(iterable, key, reverse) {
      const arr = [...iterable];
      if (key) {
        arr.sort((a, b) => {
          const ka = key(a), kb = key(b);
          return ka < kb ? -1 : ka > kb ? 1 : 0;
        });
      } else {
        arr.sort((a, b) => a < b ? -1 : a > b ? 1 : 0);
      }
      if (reverse) arr.reverse();
      return arr;
    },
    enumerate(iterable, start) {
      const s = start || 0;
      return [...iterable].map((v, i) => [s + i, v]);
    },
    zip(...iterables) {
      const arrays = iterables.map(it => [...it]);
      const len = Math.min(...arrays.map(a => a.length));
      const result = [];
      for (let i = 0; i < len; i++) {
        result.push(arrays.map(a => a[i]));
      }
      return result;
    },
    map(fn, ...iterables) {
      if (iterables.length === 1) return [...iterables[0]].map(fn);
      const arrays = iterables.map(it => [...it]);
      const len = Math.min(...arrays.map(a => a.length));
      const result = [];
      for (let i = 0; i < len; i++) {
        result.push(fn(...arrays.map(a => a[i])));
      }
      return result;
    },
    filter(fn, iterable) {
      return [...iterable].filter(fn || (x => x));
    },
    // Python subclass relation over builtin-type *tags* -- the string names
    // (`"str"`, `"int"`, ...) that `exit_builtin_type` lowers builtin types to.
    // `object` is the universal base and `bool` is a subclass of `int`; `tuple`
    // and `frozenset` share JS representations with `list`/`set`, so fold those
    // aliases to their runtime tag first. One source of truth so isinstance and
    // issubclass can never disagree.
    _tagSubclass(subTag, superTag) {
      const fold = (t) => t === "tuple" ? "list" : t === "frozenset" ? "set" : t;
      const sub = fold(subTag), sup = fold(superTag);
      if (sup === "object") return true;
      if (sub === sup) return true;
      if (sup === "int" && sub === "bool") return true;
      return false;
    },
    isinstance(o, classinfo) {
      // isinstance(x, (A, B, ...)) -- the tuple of types lowers to a JS array;
      // match if `o` is an instance of any member.
      if (Array.isArray(classinfo)) {
        for (const c of classinfo) {
          if (_jac.builtin.isinstance(o, c)) return true;
        }
        return false;
      }
      // Builtin types lower to a string tag (their Python type name): compare
      // the value's runtime type against it via the shared subclass relation.
      if (typeof classinfo === "string") {
        return _jac.builtin._tagSubclass(_jac.poly._typename(o), classinfo);
      }
      // User archetype / JS constructor.
      if (typeof classinfo === "function") return o instanceof classinfo;
      return false;
    },
    issubclass(cls, classinfo) {
      if (Array.isArray(classinfo)) {
        for (const c of classinfo) {
          if (_jac.builtin.issubclass(cls, c)) return true;
        }
        return false;
      }
      // When either side is a builtin-type tag, compare by the shared subclass
      // relation (covers `object`, `bool` ⊂ `int`, and identity).
      if (typeof cls === "string" || typeof classinfo === "string") {
        return _jac.builtin._tagSubclass(cls, classinfo);
      }
      if (cls === classinfo) return true;
      let proto = cls;
      while (proto) {
        if (proto === classinfo) return true;
        proto = Object.getPrototypeOf(proto);
      }
      return false;
    },
    id(o) {
      if (o === null || o === undefined) return 0;
      if (typeof o === "number") return o;
      return typeof o === "string" ? _jac.builtin._strHash(o) : 0;
    },
    _strHash(s) {
      let h = 0;
      for (let i = 0; i < s.length; i++) {
        h = ((h << 5) - h + s.charCodeAt(i)) | 0;
      }
      return h;
    },
    jid(obj) {
      if (obj === null || obj === undefined) {
        throw new _jac.exc.TypeError(
          "jid() called on " + String(obj)
          + " -- the server may have returned an error instead of a node."
          + " Check that your endpoint returned successfully before using jid()."
        );
      }
      if (obj._jac_id === undefined) {
        throw new _jac.exc.TypeError(
          "jid() expected a node or edge with graph identity, but received "
          + (typeof obj === "object" ? JSON.stringify(Object.keys(obj)) : typeof obj)
          + ". Only node and edge archetypes have a jid."
        );
      }
      return obj._jac_id;
    },
    // unsafe_html(x) returns a sentinel object that the JSX flattener
    // recognises and renders as raw HTML via dangerouslySetInnerHTML.
    // Named "unsafe" so the security implication is visible at the use site.
    unsafe_html(html) {
      return { __jacUnsafeHtml: String(html == null ? "" : html) };
    },
    hash(obj) { return _jac.builtin.id(obj); },
    repr(obj) { return JSON.stringify(obj); },
    pow(base, exp, mod) {
      let result = Math.pow(base, exp);
      if (mod !== undefined) result = ((result % mod) + mod) % mod;
      return result;
    },
    next(iter, default_) {
      const result = iter.next();
      if (result.done) {
        if (default_ !== undefined) return default_;
        throw new _jac.exc.StopIteration("StopIteration");
      }
      return result.value;
    },
    vars(o) {
      if (o === undefined) return {};
      const result = {};
      for (const [k, v] of Object.entries(o)) {
        if (typeof v !== "function") result[k] = v;
      }
      return result;
    },
    dir(o) {
      if (o === undefined) return [];
      return Object.getOwnPropertyNames(o).sort();
    },
    open() { throw new _jac.exc.OSError("open() is not supported in client-side JavaScript"); },
    format(value, format_spec) {
      if (!format_spec) return String(value);
      return String(value);
    },
    ascii(obj) {
      if (typeof obj === "string") {
        let r = "'";
        for (const c of obj) {
          const code = c.charCodeAt(0);
          if (c === "\\") r += "\\\\";
          else if (c === "'") r += "\\'";
          else if (code >= 0x20 && code <= 0x7e) r += c;
          else if (code < 0x100) r += "\\x" + code.toString(16).padStart(2, "0");
          else r += "\\u" + code.toString(16).padStart(4, "0");
        }
        return r + "'";
      }
      return String(obj);
    },
    complex(re, im) { return {re: re || 0, im: im || 0}; },
    bool(x) {
      if (x === null || x === undefined || x === false || x === 0 || x === "" || Number.isNaN(x)) return false;
      if (Array.isArray(x)) return x.length > 0;
      if (x instanceof Set || x instanceof Map) return x.size > 0;
      if (typeof x === "object") return Object.keys(x).length > 0;
      return true;
    },
    range(...args) {
      let start, stop, step;
      if (args.length === 1) { start = 0; stop = args[0]; step = 1; }
      else if (args.length === 2) { start = args[0]; stop = args[1]; step = 1; }
      else { start = args[0]; stop = args[1]; step = args[2]; }
      if (step === 0) throw new _jac.exc.ValueError("range() arg 3 must not be zero");
      const result = [];
      if (step > 0) { for (let i = start; i < stop; i += step) result.push(i); }
      else { for (let i = start; i > stop; i += step) result.push(i); }
      return result;
    },
    slice(...args) {
      if (args.length === 1) return { start: null, stop: args[0], step: null };
      return { start: args[0] !== undefined ? args[0] : null, stop: args[1] !== undefined ? args[1] : null, step: args[2] !== undefined ? args[2] : null };
    },
    format(value, format_spec) {
      if (!format_spec || format_spec === "") return String(value);
      const m = format_spec.match(/^([<>^]?)(\d+)?(?:\.(\d+))?([dfseboxXn%]?)$/);
      if (!m) return String(value);
      const [, align, widthStr, precStr, ftype] = m;
      let s;
      const prec = precStr !== undefined ? parseInt(precStr) : undefined;
      if (ftype === "f" || ftype === "") {
        s = prec !== undefined ? Number(value).toFixed(prec) : String(value);
      } else if (ftype === "d") {
        s = String(Math.floor(Number(value)));
      } else if (ftype === "b") {
        s = Number(value).toString(2);
      } else if (ftype === "o") {
        s = Number(value).toString(8);
      } else if (ftype === "x") {
        s = Number(value).toString(16);
      } else if (ftype === "X") {
        s = Number(value).toString(16).toUpperCase();
      } else if (ftype === "e") {
        s = prec !== undefined ? Number(value).toExponential(prec) : Number(value).toExponential();
      } else if (ftype === "%") {
        s = (prec !== undefined ? (Number(value) * 100).toFixed(prec) : (Number(value) * 100).toFixed(6)) + "%";
      } else if (ftype === "s" || ftype === "n") {
        s = String(value);
      } else {
        s = String(value);
      }
      if (widthStr) {
        const width = parseInt(widthStr);
        if (s.length < width) {
          const pad = " ".repeat(width - s.length);
          if (align === "<") s = s + pad;
          else if (align === "^") {
            const left = Math.floor((width - s.length) / 2);
            s = " ".repeat(left) + s + " ".repeat(width - s.length - left);
          }
          else s = pad + s;
        }
      }
      return s;
    }
  },

  exc: (() => {
    class BaseException extends Error { constructor(m) { super(m); this.name = 'BaseException'; } }
    class Exception extends BaseException { constructor(m) { super(m); this.name = 'Exception'; } }
    class ArithmeticError extends Exception { constructor(m) { super(m); this.name = 'ArithmeticError'; } }
    class ZeroDivisionError extends ArithmeticError { constructor(m) { super(m); this.name = 'ZeroDivisionError'; } }
    class OverflowError extends ArithmeticError { constructor(m) { super(m); this.name = 'OverflowError'; } }
    class FloatingPointError extends ArithmeticError { constructor(m) { super(m); this.name = 'FloatingPointError'; } }
    class LookupError extends Exception { constructor(m) { super(m); this.name = 'LookupError'; } }
    class IndexError extends LookupError { constructor(m) { super(m); this.name = 'IndexError'; } }
    class KeyError extends LookupError { constructor(m) { super(m); this.name = 'KeyError'; } }
    class ValueError extends Exception { constructor(m) { super(m); this.name = 'ValueError'; } }
    class TypeError_ extends Exception { constructor(m) { super(m); this.name = 'TypeError'; } }
    class AttributeError extends Exception { constructor(m) { super(m); this.name = 'AttributeError'; } }
    class RuntimeError extends Exception { constructor(m) { super(m); this.name = 'RuntimeError'; } }
    class NotImplementedError extends RuntimeError { constructor(m) { super(m); this.name = 'NotImplementedError'; } }
    class RecursionError extends RuntimeError { constructor(m) { super(m); this.name = 'RecursionError'; } }
    class OSError extends Exception { constructor(m) { super(m); this.name = 'OSError'; } }
    class FileNotFoundError extends OSError { constructor(m) { super(m); this.name = 'FileNotFoundError'; } }
    class FileExistsError extends OSError { constructor(m) { super(m); this.name = 'FileExistsError'; } }
    class PermissionError extends OSError { constructor(m) { super(m); this.name = 'PermissionError'; } }
    class TimeoutError extends OSError { constructor(m) { super(m); this.name = 'TimeoutError'; } }
    class IsADirectoryError extends OSError { constructor(m) { super(m); this.name = 'IsADirectoryError'; } }
    class NotADirectoryError extends OSError { constructor(m) { super(m); this.name = 'NotADirectoryError'; } }
    class AssertionError extends Exception { constructor(m) { super(m); this.name = 'AssertionError'; } }
    class ImportError extends Exception { constructor(m) { super(m); this.name = 'ImportError'; } }
    class ModuleNotFoundError extends ImportError { constructor(m) { super(m); this.name = 'ModuleNotFoundError'; } }
    class NameError extends Exception { constructor(m) { super(m); this.name = 'NameError'; } }
    class UnboundLocalError extends NameError { constructor(m) { super(m); this.name = 'UnboundLocalError'; } }
    class StopIteration extends Exception { constructor(m) { super(m); this.name = 'StopIteration'; } }
    class StopAsyncIteration extends Exception { constructor(m) { super(m); this.name = 'StopAsyncIteration'; } }
    class EOFError extends Exception { constructor(m) { super(m); this.name = 'EOFError'; } }
    class MemoryError extends Exception { constructor(m) { super(m); this.name = 'MemoryError'; } }
    class ReferenceError_ extends Exception { constructor(m) { super(m); this.name = 'ReferenceError'; } }
    class KeyboardInterrupt extends BaseException { constructor(m) { super(m); this.name = 'KeyboardInterrupt'; } }
    class SystemExit extends BaseException { constructor(m) { super(m); this.name = 'SystemExit'; } }
    class GeneratorExit extends BaseException { constructor(m) { super(m); this.name = 'GeneratorExit'; } }
    const _all = { BaseException, Exception, ArithmeticError, ZeroDivisionError, OverflowError, FloatingPointError, LookupError, IndexError, KeyError, ValueError, TypeError: TypeError_, AttributeError, RuntimeError, NotImplementedError, RecursionError, OSError, FileNotFoundError, FileExistsError, PermissionError, TimeoutError, IsADirectoryError, NotADirectoryError, AssertionError, ImportError, ModuleNotFoundError, NameError, UnboundLocalError, StopIteration, StopAsyncIteration, EOFError, MemoryError, ReferenceError: ReferenceError_, KeyboardInterrupt, SystemExit, GeneratorExit };
    function matches(e, name) {
      if (name === 'BaseException') return true;
      if (name === 'Exception') return (e instanceof _all.Exception) || !(e instanceof _all.BaseException);
      const cls = _all[name];
      return cls ? (e instanceof cls) : false;
    }
    return Object.assign(_all, { matches });
  })()
};
function comment_matches(c, f) {
  if ((f === "open")) {
    return !c["resolved"];
  }
  if ((f === "resolved")) {
    return c["resolved"];
  }
  if ((f === "mine")) {
    for (const m of c["thread"]) {
      if ((m["author"] === "You")) {
        return true;
      }
    }
    return false;
  }
  return true;
}
function Tab_btn(props) {
  const {active, label, icon, show_label, onClick} = props;
  return __jacJsx("button", {"onClick": onClick, "title": label, "className": cn("flex flex-1 items-center justify-center gap-1.5 border-b-2 py-2.5 text-xs font-medium transition-colors", (show_label ? "min-w-0 px-1.5" : "px-1"), (active ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"))}, [icon, __jacJsx("span", {"className": (show_label ? "truncate" : "sr-only")}, [label])]);
}
const tab_btn = Tab_btn;
function Section(props) {
  const {title} = props;
  return __jacJsx("div", {"className": "mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground"}, [title]);
}
const section = Section;
function Arrange_btn(props) {
  const {op, label, icon, disabled, onArrange} = props;
  return __jacJsx("button", {"aria-label": label, "title": (disabled ? (label + " (needs 3+ objects)") : label), "disabled": disabled, "onClick": _e => {
    if ((onArrange && !disabled)) {
      onArrange(op);
    }
  }, "className": cn("flex size-8 items-center justify-center rounded-md border border-border", (disabled ? "cursor-not-allowed opacity-40" : "text-muted-foreground hover:border-primary hover:bg-primary-soft hover:text-primary"))}, [icon]);
}
const arrange_btn = Arrange_btn;
function Swatch(props) {
  const {color, active, onClick} = props;
  return __jacJsx("button", {"key": color, "title": color, "onClick": onClick, "className": cn("size-6 rounded-md border transition-transform hover:scale-110", (active ? "border-primary ring-2 ring-primary/40" : "border-border")), "style": {"backgroundColor": color}}, []);
}
const swatch = Swatch;
function Width_btn(props) {
  const {w, active, onClick} = props;
  return __jacJsx("button", {"onClick": onClick, "className": cn("flex h-8 flex-1 items-center justify-center rounded-md border text-sm transition-colors", (active ? "border-primary bg-primary-soft text-primary" : "border-border hover:bg-accent"))}, [String(w)]);
}
const width_btn = Width_btn;
function Font_btn(props) {
  const {fkey, label, active, onClick} = props;
  return __jacJsx("button", {"onClick": onClick, "title": label, "aria-pressed": active, "data-font": fkey, "style": {"fontFamily": fontStack(fkey)}, "className": cn("flex h-8 flex-1 items-center justify-center rounded-md border text-sm transition-colors", (active ? "border-primary bg-primary-soft text-primary" : "border-border hover:bg-accent"))}, [label]);
}
const font_btn = Font_btn;
function Seg_btn(props) {
  const {active, label, onClick} = props;
  return __jacJsx("button", {"onClick": onClick, "className": cn("flex h-8 flex-1 items-center justify-center gap-1 rounded-md border text-sm transition-colors", (active ? "border-primary bg-primary-soft font-medium text-primary" : "border-border text-muted-foreground hover:bg-accent"))}, [label]);
}
const seg_btn = Seg_btn;
function Layer_icon(props) {
  const {kind} = props;
  if ((kind === "ellipse")) {
    return __jacJsx(Circle, {"size": 14}, []);
  }
  if ((kind === "diamond")) {
    return __jacJsx(Diamond, {"size": 14}, []);
  }
  if ((kind === "cylinder")) {
    return __jacJsx(Cylinder, {"size": 14}, []);
  }
  if ((kind === "triangle")) {
    return __jacJsx(Triangle, {"size": 14}, []);
  }
  if ((kind === "hexagon")) {
    return __jacJsx(Hexagon, {"size": 14}, []);
  }
  if ((kind === "text")) {
    return __jacJsx(Type, {"size": 14}, []);
  }
  if ((kind === "sticky")) {
    return __jacJsx(StickyNote, {"size": 14}, []);
  }
  if ((kind === "connector")) {
    return __jacJsx(Spline, {"size": 14}, []);
  }
  return __jacJsx(Square, {"size": 14}, []);
}
const layer_icon = Layer_icon;
function RightPanel(props) {
  const {panelWidth: panelWidth = 320, activeTab: activeTab = "properties", theme: theme = "light", gridOn: gridOn = true, onTab: onTab = null, onSetTheme: onSetTheme = null, onToggleGrid: onToggleGrid = null, onCollapse: onCollapse = null, selected: selected = null, onStyle: onStyle = null, elements: elements = [], selectedId: selectedId = null, onSelectLayer: onSelectLayer = null, onDeleteLayer: onDeleteLayer = null, pageName: pageName = "Page 1", onRenamePage: onRenamePage = null, onExport: onExport = null, comments: comments = [], activeCommentId: activeCommentId = null, onOpenComment: onOpenComment = null, onReply: onReply = null, onResolve: onResolve = null, onDeleteComment: onDeleteComment = null, onStartComment: onStartComment = null, onSharePage: onSharePage = null, pageShared: pageShared = false, pageAccessLabel: pageAccessLabel = "Private", selectedIds: selectedIds = [], onSelectGroup: onSelectGroup = null, onRenameGroup: onRenameGroup = null, onDeleteGroup: onDeleteGroup = null, editable: editable = true, onShowConnected: onShowConnected = null, onArrange: onArrange = null, onOrder: onOrder = null, onToggleLock: onToggleLock = null, onToggleHide: onToggleHide = null} = props;
  const [commentFilter, setCommentFilter] = useState("all");
  const [replyDraft, setReplyDraft] = useState("");
  const [expandedGroups, setExpandedGroups] = useState({});
  const [renamingGroup, setRenamingGroup] = useState("");
  const [renameDraft, setRenameDraft] = useState("");
  function id_selected(id) {
    if ((id === selectedId)) {
      return true;
    }
    for (const s of selectedIds) {
      if ((s === id)) {
        return true;
      }
    }
    return false;
  }
  function el_group(e) {
    return (_jac.poly.contains(e, "groupId") ? e["groupId"] : "");
  }
  function grp_name(gid) {
    for (const e of elements) {
      if ((el_group(e) === gid)) {
        return (_jac.poly.contains(e, "groupName") ? e["groupName"] : "Group");
      }
    }
    return "Group";
  }
  function grp_count(gid) {
    let n = 0;
    for (const e of elements) {
      if ((el_group(e) === gid)) {
        n = (n + 1);
      }
    }
    return n;
  }
  function grp_selected(gid) {
    for (const e of elements) {
      if (((el_group(e) === gid) && id_selected(e["id"]))) {
        return true;
      }
    }
    return false;
  }
  function grp_expanded(gid) {
    return (_jac.poly.contains(expandedGroups, gid) && _jac.poly.getitem(expandedGroups, gid));
  }
  function toggle_group(gid) {
    let m = {...expandedGroups};
    m[gid] = !grp_expanded(gid);
    setExpandedGroups(m);
  }
  function layer_rows() {
    let rows = [];
    let seen = {};
    for (const i of _jac.builtin.range((elements.length - 1), (-1), (-1))) {
      let el = _jac.poly.getitem(elements, i);
      let gid = el_group(el);
      if (gid) {
        if (!(gid in seen)) {
          seen[gid] = true;
          rows = [...rows, ...[{"t": "group", "gid": gid}]];
          if (grp_expanded(gid)) {
            for (const j of _jac.builtin.range((elements.length - 1), (-1), (-1))) {
              let m = _jac.poly.getitem(elements, j);
              if ((el_group(m) === gid)) {
                rows = [...rows, ...[{"t": "member", "el": m}]];
              }
            }
          }
        }
      } else {
        rows = [...rows, ...[{"t": "item", "el": el}]];
      }
    }
    return rows;
  }
  function leaf_row(props) {
    const {el, indented} = props;
    return __jacJsx("div", {"key": el["id"], "onClick": _e => {
      if (onSelectLayer) {
        onSelectLayer(el["id"]);
      }
    }, "className": cn("group flex cursor-pointer items-center gap-2 rounded-md py-1.5 pr-2 text-sm transition-colors", (indented ? "pl-7" : "pl-2"), (id_selected(el["id"]) ? "bg-primary-soft text-primary" : "hover:bg-accent"))}, [__jacJsx("span", {"className": "text-muted-foreground"}, [layer_icon({kind: el["kind"]})]), __jacJsx("span", {"className": cn("flex-1 truncate capitalize", (el["hidden"] ? "italic opacity-50" : ""))}, [(el["text"] ? el["text"] : el["kind"])]), __jacJsx("button", {"title": (el["hidden"] ? "Show" : "Hide"), "aria-label": (el["hidden"] ? "Show object" : "Hide object"), "onClick": _e => {
      _e.stopPropagation();
      if (onToggleHide) {
        onToggleHide(el["id"]);
      }
    }, "className": cn("flex size-6 items-center justify-center rounded text-muted-foreground transition-opacity hover:bg-accent hover:text-foreground", (el["hidden"] ? "opacity-100" : "opacity-0 group-hover:opacity-100"))}, [(el["hidden"] ? __jacJsx(EyeOff, {"size": 13}, []) : __jacJsx(Eye, {"size": 13}, []))]), __jacJsx("button", {"title": (el["locked"] ? "Unlock" : "Lock"), "aria-label": (el["locked"] ? "Unlock object" : "Lock object"), "onClick": _e => {
      _e.stopPropagation();
      if (onToggleLock) {
        onToggleLock(el["id"]);
      }
    }, "className": cn("flex size-6 items-center justify-center rounded transition-opacity hover:bg-accent hover:text-foreground", (el["locked"] ? "text-primary opacity-100" : "text-muted-foreground opacity-0 group-hover:opacity-100"))}, [(el["locked"] ? __jacJsx(Lock, {"size": 13}, []) : __jacJsx(LockOpen, {"size": 13}, []))]), __jacJsx("button", {"title": "Delete", "onClick": _e => {
      _e.stopPropagation();
      if (onDeleteLayer) {
        onDeleteLayer(el["id"]);
      }
    }, "className": "flex size-6 items-center justify-center rounded text-muted-foreground opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100"}, [__jacJsx(Trash2, {"size": 13}, [])])]);
  }
  function group_row(props) {
    const {gid} = props;
    return __jacJsx("div", {"key": ("g-" + gid), "className": cn("group flex items-center gap-1.5 rounded-md py-1.5 pl-1 pr-2 text-sm transition-colors", (grp_selected(gid) ? "bg-primary-soft text-primary" : "hover:bg-accent"))}, [__jacJsx("button", {"title": (grp_expanded(gid) ? "Collapse" : "Expand"), "onClick": _e => {
      _e.stopPropagation();
      toggle_group(gid);
    }, "className": "flex size-5 items-center justify-center rounded text-muted-foreground hover:text-foreground"}, [(grp_expanded(gid) ? __jacJsx(ChevronDown, {"size": 14}, []) : __jacJsx(ChevronRight, {"size": 14}, []))]), __jacJsx(Folder, {"size": 14, "className": "shrink-0 text-muted-foreground"}, []), ((renamingGroup === gid) ? __jacJsx("input", {"autoFocus": true, "value": renameDraft, "onChange": e => {
      setRenameDraft(e.target.value);
    }, "onClick": e => {
      e.stopPropagation();
    }, "onKeyDown": e => {
      if ((e.key === "Enter")) {
        if (onRenameGroup) {
          onRenameGroup(gid, renameDraft);
        }
        setRenamingGroup("");
      } else if ((e.key === "Escape")) {
        setRenamingGroup("");
      }
    }, "onBlur": _e => {
      if (onRenameGroup) {
        onRenameGroup(gid, renameDraft);
      }
      setRenamingGroup("");
    }, "className": "h-6 min-w-0 flex-1 rounded border border-primary bg-background px-1.5 text-sm outline-none"}, []) : __jacJsx("span", {"onClick": _e => {
      if (onSelectGroup) {
        onSelectGroup(gid);
      }
    }, "onDoubleClick": _e => {
      setRenameDraft(grp_name(gid));
      setRenamingGroup(gid);
    }, "className": "flex-1 cursor-pointer truncate font-medium", "title": "Click to select · double-click to rename"}, [grp_name(gid)])), __jacJsx("span", {"className": "shrink-0 text-xs text-muted-foreground"}, [String(grp_count(gid))]), __jacJsx("button", {"title": "Delete group", "onClick": _e => {
      _e.stopPropagation();
      if (onDeleteGroup) {
        onDeleteGroup(gid);
      }
    }, "className": "flex size-6 items-center justify-center rounded text-muted-foreground opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100"}, [__jacJsx(Trash2, {"size": 13}, [])])]);
  }
  function render_row(props) {
    const {r} = props;
    if ((r["t"] === "group")) {
      return group_row({gid: r["gid"]});
    }
    return leaf_row({el: r["el"], indented: (r["t"] === "member")});
  }
  function comment_chip(props) {
    const {f, label} = props;
    return __jacJsx("button", {"onClick": _e => {
      setCommentFilter(f);
    }, "className": cn("rounded-full px-2.5 py-1 text-xs transition-colors", ((commentFilter === f) ? "bg-primary font-medium text-primary-foreground" : "text-muted-foreground hover:bg-accent"))}, [label]);
  }
  function comment_row(props) {
    const {c, idx} = props;
    let is_active = (c["id"] === activeCommentId);
    let first = ((c["thread"].length > 0) ? c["thread"][0]["text"] : "New comment");
    return __jacJsx("div", {"key": c["id"], "className": cn("rounded-lg border p-2.5 transition-colors", (is_active ? "border-primary bg-primary-soft/40" : "border-border hover:border-primary/50"))}, [__jacJsx("button", {"onClick": _e => {
      if (onOpenComment) {
        onOpenComment(c["id"]);
      }
    }, "className": "flex w-full items-start gap-2 text-left"}, [__jacJsx("span", {"className": cn("mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold", (c["resolved"] ? "bg-muted text-muted-foreground" : "bg-primary text-primary-foreground"))}, [(c["resolved"] ? __jacJsx(Check, {"size": 11}, []) : String((idx + 1)))]), __jacJsx("span", {"className": cn("flex-1 text-sm", (c["resolved"] ? "text-muted-foreground line-through" : ""))}, [first])]), (is_active ? __jacJsx("div", {"className": "mt-2 flex flex-col gap-2 pl-7"}, [_jac.builtin.range(c["thread"].length).map(j => __jacJsx("div", {"key": String(j), "className": "text-sm leading-snug"}, [__jacJsx("span", {"className": "font-medium"}, [_jac.poly.getitem(c["thread"], j)["author"]]), __jacJsx("span", {"className": "text-muted-foreground"}, [" · "]), __jacJsx("span", {}, [_jac.poly.getitem(c["thread"], j)["text"]])])), __jacJsx("div", {"className": "flex items-center gap-1.5"}, [__jacJsx("input", {"value": replyDraft, "onChange": e => {
      setReplyDraft(e.target.value);
    }, "onKeyDown": e => {
      if (((e.key === "Enter") && onReply)) {
        onReply(c["id"], replyDraft);
        setReplyDraft("");
      }
    }, "placeholder": "Reply...", "className": "h-8 flex-1 rounded-md border border-input bg-background px-2 text-sm outline-none focus:border-primary"}, []), __jacJsx("button", {"title": "Send", "onClick": _e => {
      if (onReply) {
        onReply(c["id"], replyDraft);
        setReplyDraft("");
      }
    }, "className": "flex size-8 items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90"}, [__jacJsx(Send, {"size": 14}, [])])]), __jacJsx("div", {"className": "flex gap-3"}, [__jacJsx("button", {"onClick": _e => {
      if (onResolve) {
        onResolve(c["id"]);
      }
    }, "className": "flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"}, [__jacJsx(Check, {"size": 13}, []), " ", (c["resolved"] ? "Reopen" : "Resolve")]), __jacJsx("button", {"onClick": _e => {
      if (onDeleteComment) {
        onDeleteComment(c["id"]);
      }
    }, "className": "flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive"}, [__jacJsx(Trash2, {"size": 13}, []), " Delete"])])]) : null)]);
  }
  let has_sel = (selected !== null);
  let is_conn = (has_sel && (selected["kind"] === "connector"));
  let is_multi = (has_sel && (selected["kind"] === "multiple"));
  let multi_n = (is_multi ? selected["_multi"] : 0);
  let sel_opacity = ((has_sel && selected["opacity"]) ? selected["opacity"] : 1);
  let sel_opacity_pct = Math.trunc(Number((sel_opacity * 100)));
  let sel_kind = (has_sel ? selected["kind"] : "");
  let sel_text = ((has_sel && selected["text"]) ? String(selected["text"]) : "");
  let can_type = (has_sel && ((((sel_kind === "text") || (sel_kind === "sticky")) || (sel_kind === "table")) || (sel_text !== "")));
  let sel_font = ((has_sel && selected["fontFamily"]) ? selected["fontFamily"] : "sans");
  let sel_fsize = ((has_sel && selected["fontSize"]) ? Math.trunc(Number(selected["fontSize"])) : ((sel_kind === "sticky") ? 14 : 16));
  let c_routing = ((is_conn && selected["routing"]) ? selected["routing"] : "straight");
  let c_dashed = (is_conn && (selected["dashed"] === true));
  let c_dotted = (is_conn && (selected["dotted"] === true));
  let c_solid = ((is_conn && !c_dashed) && !c_dotted);
  let c_start = (is_conn && (selected["startArrow"] === true));
  let c_end = (is_conn && (selected["endArrow"] !== false));
  let c_endHead = ((is_conn && selected["endHead"]) ? selected["endHead"] : (c_end ? "arrow" : "none"));
  let c_startHead = ((is_conn && selected["startHead"]) ? selected["startHead"] : (c_start ? "arrow" : "none"));
  let track = cn("inline-flex h-5 w-9 items-center rounded-full transition-colors", (gridOn ? "bg-primary" : "bg-input"));
  let knob = cn("inline-block size-4 rounded-full bg-white shadow transition-transform", (gridOn ? "translate-x-[18px]" : "translate-x-0.5"));
  let seg_l = cn("flex size-6 items-center justify-center rounded-md transition-colors", ((theme === "light") ? "bg-accent text-foreground shadow-sm" : "text-muted-foreground"));
  let seg_d = cn("flex size-6 items-center justify-center rounded-md transition-colors", ((theme === "dark") ? "bg-accent text-foreground shadow-sm" : "text-muted-foreground"));
  let wide = (panelWidth >= 400);
  return __jacJsx("aside", {"className": "flex h-full w-full flex-col border-l border-border bg-card"}, [__jacJsx("div", {"className": "flex h-11 shrink-0 items-center justify-between pl-1 pr-2"}, [__jacJsx("div", {"className": "flex flex-1"}, [tab_btn({active: (activeTab === "properties"), label: "Properties", icon: __jacJsx(SlidersHorizontal, {"size": 13}, []), show_label: wide, onClick: _e => {
    if (onTab) {
      onTab("properties");
    }
  }}), tab_btn({active: (activeTab === "layers"), label: "Layers", icon: __jacJsx(Layers, {"size": 13}, []), show_label: wide, onClick: _e => {
    if (onTab) {
      onTab("layers");
    }
  }}), tab_btn({active: (activeTab === "comments"), label: "Comments", icon: __jacJsx(MessageSquare, {"size": 13}, []), show_label: wide, onClick: _e => {
    if (onTab) {
      onTab("comments");
    }
  }}), tab_btn({active: (activeTab === "ai"), label: "AI", icon: __jacJsx(Sparkles, {"size": 13}, []), show_label: wide, onClick: _e => {
    if (onTab) {
      onTab("ai");
    }
  }})]), __jacJsx("button", {"title": "Collapse panel", "onClick": onCollapse, "className": "ml-1 flex size-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"}, [__jacJsx(PanelRightClose, {"size": 16}, [])])]), __jacJsx("div", {"className": "scroll-thin flex-1 overflow-y-auto p-3.5"}, [(((activeTab === "properties") && !editable) ? __jacJsx("div", {"className": "mb-3 rounded-lg border border-border bg-muted px-2.5 py-2 text-xs text-muted-foreground"}, ["View only - you don't have edit access to this diagram."]) : null), ((((activeTab === "properties") && has_sel) && !is_conn) ? __jacJsx("div", {"className": ("flex flex-col gap-5" + (editable ? "" : " pointer-events-none select-none opacity-60"))}, [__jacJsx("div", {}, [section({title: (is_multi ? "Selection" : "Element")}), __jacJsx("div", {"className": "rounded-lg border border-border px-2.5 py-2 text-sm text-muted-foreground"}, [__jacJsx("span", {"className": (is_multi ? "" : "capitalize")}, [(is_multi ? (String(multi_n) + " objects selected") : (has_sel ? selected["kind"] : ""))])]), (!is_multi ? __jacJsx("button", {"data-testid": "select-connected", "onClick": _e => {
    if (onShowConnected) {
      onShowConnected(selectedId);
    }
  }, "title": "Select every shape this one connects to", "className": "mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:border-primary hover:bg-primary-soft hover:text-primary"}, [__jacJsx(Spline, {"size": 13}, []), " Select connected"]) : null)]), ((has_sel && editable) ? __jacJsx("div", {"data-testid": "order", "className": "flex flex-col gap-2"}, [section({title: "Order & visibility"}), __jacJsx("div", {"className": "flex flex-wrap gap-1"}, [arrange_btn({op: "__front__", label: "Bring to front", icon: __jacJsx(BringToFront, {"size": 15}, []), disabled: false, onArrange: _op => {
    if (onOrder) {
      onOrder("toFront");
    }
  }}), arrange_btn({op: "__fwd__", label: "Bring forward", icon: __jacJsx(MoveUp, {"size": 15}, []), disabled: false, onArrange: _op => {
    if (onOrder) {
      onOrder("forward");
    }
  }}), arrange_btn({op: "__bwd__", label: "Send backward", icon: __jacJsx(MoveDown, {"size": 15}, []), disabled: false, onArrange: _op => {
    if (onOrder) {
      onOrder("backward");
    }
  }}), arrange_btn({op: "__back__", label: "Send to back", icon: __jacJsx(SendToBack, {"size": 15}, []), disabled: false, onArrange: _op => {
    if (onOrder) {
      onOrder("toBack");
    }
  }}), arrange_btn({op: "__lock__", label: "Lock selection", icon: __jacJsx(Lock, {"size": 15}, []), disabled: false, onArrange: _op => {
    if (onToggleLock) {
      onToggleLock(null);
    }
  }}), arrange_btn({op: "__hide__", label: "Hide selection", icon: __jacJsx(EyeOff, {"size": 15}, []), disabled: false, onArrange: _op => {
    if (onToggleHide) {
      onToggleHide(null);
    }
  }})])]) : null), ((is_multi && editable) ? __jacJsx("div", {"data-testid": "arrange", "className": "flex flex-col gap-2"}, [section({title: "Arrange"}), __jacJsx("div", {"className": "flex flex-wrap gap-1"}, [arrange_btn({op: "left", label: "Align left", icon: __jacJsx(AlignStartVertical, {"size": 15}, []), disabled: false, onArrange: onArrange}), arrange_btn({op: "hcenter", label: "Align horizontal centers", icon: __jacJsx(AlignCenterVertical, {"size": 15}, []), disabled: false, onArrange: onArrange}), arrange_btn({op: "right", label: "Align right", icon: __jacJsx(AlignEndVertical, {"size": 15}, []), disabled: false, onArrange: onArrange}), arrange_btn({op: "top", label: "Align top", icon: __jacJsx(AlignStartHorizontal, {"size": 15}, []), disabled: false, onArrange: onArrange}), arrange_btn({op: "vmiddle", label: "Align vertical centers", icon: __jacJsx(AlignCenterHorizontal, {"size": 15}, []), disabled: false, onArrange: onArrange}), arrange_btn({op: "bottom", label: "Align bottom", icon: __jacJsx(AlignEndHorizontal, {"size": 15}, []), disabled: false, onArrange: onArrange}), arrange_btn({op: "disth", label: "Distribute horizontally", icon: __jacJsx(AlignHorizontalDistributeCenter, {"size": 15}, []), disabled: (multi_n < 3), onArrange: onArrange}), arrange_btn({op: "distv", label: "Distribute vertically", icon: __jacJsx(AlignVerticalDistributeCenter, {"size": 15}, []), disabled: (multi_n < 3), onArrange: onArrange})]), __jacJsx("div", {"className": "flex gap-1"}, [arrange_btn({op: "matchw", label: "Match width", icon: __jacJsx("span", {"className": "text-xs font-semibold"}, ["W"]), disabled: false, onArrange: onArrange}), arrange_btn({op: "matchh", label: "Match height", icon: __jacJsx("span", {"className": "text-xs font-semibold"}, ["H"]), disabled: false, onArrange: onArrange}), arrange_btn({op: "matchboth", label: "Match size", icon: __jacJsx("span", {"className": "text-xs font-semibold"}, ["W·H"]), disabled: false, onArrange: onArrange})])]) : null), __jacJsx("div", {}, [__jacJsx("span", {"className": "mb-2 block text-xs text-muted-foreground"}, ["Fill"]), __jacJsx("div", {"className": "flex flex-wrap gap-1.5"}, [swatch({color: "transparent", active: (has_sel && (selected["fill"] === "transparent")), onClick: _e => {
    if (onStyle) {
      onStyle({"fill": "transparent"});
    }
  }}), swatch({color: "#eef2ff", active: (has_sel && (selected["fill"] === "#eef2ff")), onClick: _e => {
    if (onStyle) {
      onStyle({"fill": "#eef2ff"});
    }
  }}), swatch({color: "#dcfce7", active: (has_sel && (selected["fill"] === "#dcfce7")), onClick: _e => {
    if (onStyle) {
      onStyle({"fill": "#dcfce7"});
    }
  }}), swatch({color: "#fef9c3", active: (has_sel && (selected["fill"] === "#fef9c3")), onClick: _e => {
    if (onStyle) {
      onStyle({"fill": "#fef9c3"});
    }
  }}), swatch({color: "#fee2e2", active: (has_sel && (selected["fill"] === "#fee2e2")), onClick: _e => {
    if (onStyle) {
      onStyle({"fill": "#fee2e2"});
    }
  }}), swatch({color: "#e0e7ff", active: (has_sel && (selected["fill"] === "#e0e7ff")), onClick: _e => {
    if (onStyle) {
      onStyle({"fill": "#e0e7ff"});
    }
  }})])]), __jacJsx("div", {}, [__jacJsx("span", {"className": "mb-2 block text-xs text-muted-foreground"}, ["Stroke"]), __jacJsx("div", {"className": "flex flex-wrap gap-1.5"}, [STROKE_SWATCHES.map(sc => swatch({color: sc, active: (has_sel && (selected["stroke"] === sc)), onClick: _e => {
    if (onStyle) {
      onStyle({"stroke": sc});
    }
  }}))])]), __jacJsx("div", {}, [__jacJsx("span", {"className": "mb-2 block text-xs text-muted-foreground"}, ["Stroke width"]), __jacJsx("div", {"className": "flex gap-1.5"}, [width_btn({w: 1, active: (has_sel && (selected["strokeWidth"] === 1)), onClick: _e => {
    if (onStyle) {
      onStyle({"strokeWidth": 1});
    }
  }}), width_btn({w: 2, active: (has_sel && (selected["strokeWidth"] === 2)), onClick: _e => {
    if (onStyle) {
      onStyle({"strokeWidth": 2});
    }
  }}), width_btn({w: 3, active: (has_sel && (selected["strokeWidth"] === 3)), onClick: _e => {
    if (onStyle) {
      onStyle({"strokeWidth": 3});
    }
  }}), width_btn({w: 4, active: (has_sel && (selected["strokeWidth"] === 4)), onClick: _e => {
    if (onStyle) {
      onStyle({"strokeWidth": 4});
    }
  }})])]), __jacJsx("div", {}, [__jacJsx("span", {"className": "mb-2 flex items-center justify-between text-xs text-muted-foreground"}, [__jacJsx("span", {}, ["Opacity"]), __jacJsx("span", {}, [(String(sel_opacity_pct) + "%")])]), __jacJsx("input", {"type": "range", "min": "10", "max": "100", "step": "10", "value": String(sel_opacity_pct), "onChange": e => {
    if (onStyle) {
      onStyle({"opacity": (Math.trunc(Number(e.target.value)) / 100)});
    }
  }, "className": "w-full accent-primary"}, [])]), (can_type ? __jacJsx("div", {}, [__jacJsx("span", {"className": "mb-2 block text-xs text-muted-foreground"}, ["Font"]), __jacJsx("div", {"className": "flex gap-1.5"}, [font_btn({fkey: "sans", label: "Sans", active: (sel_font === "sans"), onClick: _e => {
    if (onStyle) {
      onStyle({"fontFamily": "sans"});
    }
  }}), font_btn({fkey: "serif", label: "Serif", active: (sel_font === "serif"), onClick: _e => {
    if (onStyle) {
      onStyle({"fontFamily": "serif"});
    }
  }}), font_btn({fkey: "mono", label: "Mono", active: (sel_font === "mono"), onClick: _e => {
    if (onStyle) {
      onStyle({"fontFamily": "mono"});
    }
  }}), font_btn({fkey: "hand", label: "Hand", active: (sel_font === "hand"), onClick: _e => {
    if (onStyle) {
      onStyle({"fontFamily": "hand"});
    }
  }})]), __jacJsx("span", {"className": "mb-2 mt-3 block text-xs text-muted-foreground"}, ["Text size"]), __jacJsx("div", {"className": "flex gap-1.5"}, [width_btn({w: 12, active: (sel_fsize === 12), onClick: _e => {
    if (onStyle) {
      onStyle({"fontSize": 12});
    }
  }}), width_btn({w: 14, active: (sel_fsize === 14), onClick: _e => {
    if (onStyle) {
      onStyle({"fontSize": 14});
    }
  }}), width_btn({w: 16, active: (sel_fsize === 16), onClick: _e => {
    if (onStyle) {
      onStyle({"fontSize": 16});
    }
  }}), width_btn({w: 20, active: (sel_fsize === 20), onClick: _e => {
    if (onStyle) {
      onStyle({"fontSize": 20});
    }
  }}), width_btn({w: 28, active: (sel_fsize === 28), onClick: _e => {
    if (onStyle) {
      onStyle({"fontSize": 28});
    }
  }})])]) : null)]) : null), (((activeTab === "properties") && is_conn) ? __jacJsx("div", {"className": ("flex flex-col gap-5" + (editable ? "" : " pointer-events-none select-none opacity-60"))}, [__jacJsx("div", {}, [section({title: "Connector"}), __jacJsx("div", {"className": "grid grid-cols-2 gap-1.5"}, [seg_btn({active: (c_routing === "straight"), label: "Straight", onClick: _e => {
    if (onStyle) {
      onStyle({"routing": "straight"});
    }
  }}), seg_btn({active: (c_routing === "orthogonal"), label: "Elbow", onClick: _e => {
    if (onStyle) {
      onStyle({"routing": "orthogonal"});
    }
  }}), seg_btn({active: (c_routing === "curved"), label: "Curved", onClick: _e => {
    if (onStyle) {
      onStyle({"routing": "curved"});
    }
  }}), seg_btn({active: (c_routing === "avoid"), label: "Smart", onClick: _e => {
    if (onStyle) {
      onStyle({"routing": "avoid"});
    }
  }})])]), __jacJsx("div", {}, [__jacJsx("span", {"className": "mb-2 block text-xs text-muted-foreground"}, ["Start cap"]), __jacJsx("div", {"className": "grid grid-cols-4 gap-1.5"}, [seg_btn({active: (c_startHead === "none"), label: "-", onClick: _e => {
    if (onStyle) {
      onStyle({"startHead": "none", "startArrow": false});
    }
  }}), seg_btn({active: (c_startHead === "arrow"), label: "\u25c0", onClick: _e => {
    if (onStyle) {
      onStyle({"startHead": "arrow", "startArrow": true});
    }
  }}), seg_btn({active: (c_startHead === "circle"), label: "\u25cb", onClick: _e => {
    if (onStyle) {
      onStyle({"startHead": "circle", "startArrow": false});
    }
  }}), seg_btn({active: (c_startHead === "diamond"), label: "\u25c6", onClick: _e => {
    if (onStyle) {
      onStyle({"startHead": "diamond", "startArrow": false});
    }
  }})])]), __jacJsx("div", {}, [__jacJsx("span", {"className": "mb-2 block text-xs text-muted-foreground"}, ["End cap"]), __jacJsx("div", {"className": "grid grid-cols-4 gap-1.5"}, [seg_btn({active: (c_endHead === "none"), label: "-", onClick: _e => {
    if (onStyle) {
      onStyle({"endHead": "none", "endArrow": false});
    }
  }}), seg_btn({active: (c_endHead === "arrow"), label: "\u25b6", onClick: _e => {
    if (onStyle) {
      onStyle({"endHead": "arrow", "endArrow": true});
    }
  }}), seg_btn({active: (c_endHead === "circle"), label: "\u25cb", onClick: _e => {
    if (onStyle) {
      onStyle({"endHead": "circle", "endArrow": false});
    }
  }}), seg_btn({active: (c_endHead === "diamond"), label: "\u25c6", onClick: _e => {
    if (onStyle) {
      onStyle({"endHead": "diamond", "endArrow": false});
    }
  }})])]), __jacJsx("div", {}, [__jacJsx("span", {"className": "mb-2 block text-xs text-muted-foreground"}, ["Relationship (ER) - start"]), __jacJsx("div", {"className": "grid grid-cols-5 gap-1.5"}, [seg_btn({active: (c_startHead === "one"), label: "1", onClick: _e => {
    if (onStyle) {
      onStyle({"startHead": "one", "startArrow": false});
    }
  }}), seg_btn({active: (c_startHead === "many"), label: "N", onClick: _e => {
    if (onStyle) {
      onStyle({"startHead": "many", "startArrow": false});
    }
  }}), seg_btn({active: (c_startHead === "zeroOrOne"), label: "0\u00b71", onClick: _e => {
    if (onStyle) {
      onStyle({"startHead": "zeroOrOne", "startArrow": false});
    }
  }}), seg_btn({active: (c_startHead === "oneOrMany"), label: "1\u00b7N", onClick: _e => {
    if (onStyle) {
      onStyle({"startHead": "oneOrMany", "startArrow": false});
    }
  }}), seg_btn({active: (c_startHead === "zeroOrMany"), label: "0\u00b7N", onClick: _e => {
    if (onStyle) {
      onStyle({"startHead": "zeroOrMany", "startArrow": false});
    }
  }})])]), __jacJsx("div", {}, [__jacJsx("span", {"className": "mb-2 block text-xs text-muted-foreground"}, ["Relationship (ER) - end"]), __jacJsx("div", {"className": "grid grid-cols-5 gap-1.5"}, [seg_btn({active: (c_endHead === "one"), label: "1", onClick: _e => {
    if (onStyle) {
      onStyle({"endHead": "one", "endArrow": false});
    }
  }}), seg_btn({active: (c_endHead === "many"), label: "N", onClick: _e => {
    if (onStyle) {
      onStyle({"endHead": "many", "endArrow": false});
    }
  }}), seg_btn({active: (c_endHead === "zeroOrOne"), label: "0\u00b71", onClick: _e => {
    if (onStyle) {
      onStyle({"endHead": "zeroOrOne", "endArrow": false});
    }
  }}), seg_btn({active: (c_endHead === "oneOrMany"), label: "1\u00b7N", onClick: _e => {
    if (onStyle) {
      onStyle({"endHead": "oneOrMany", "endArrow": false});
    }
  }}), seg_btn({active: (c_endHead === "zeroOrMany"), label: "0\u00b7N", onClick: _e => {
    if (onStyle) {
      onStyle({"endHead": "zeroOrMany", "endArrow": false});
    }
  }})])]), __jacJsx("div", {}, [__jacJsx("span", {"className": "mb-2 block text-xs text-muted-foreground"}, ["Line"]), __jacJsx("div", {"className": "grid grid-cols-3 gap-1.5"}, [seg_btn({active: c_solid, label: "Solid", onClick: _e => {
    if (onStyle) {
      onStyle({"dashed": false, "dotted": false});
    }
  }}), seg_btn({active: c_dashed, label: "Dashed", onClick: _e => {
    if (onStyle) {
      onStyle({"dashed": true, "dotted": false});
    }
  }}), seg_btn({active: c_dotted, label: "Dotted", onClick: _e => {
    if (onStyle) {
      onStyle({"dotted": true, "dashed": false});
    }
  }})])]), __jacJsx("div", {}, [__jacJsx("span", {"className": "mb-2 block text-xs text-muted-foreground"}, ["Stroke"]), __jacJsx("div", {"className": "flex flex-wrap gap-1.5"}, [STROKE_SWATCHES.map(sc => swatch({color: sc, active: (has_sel && (selected["stroke"] === sc)), onClick: _e => {
    if (onStyle) {
      onStyle({"stroke": sc});
    }
  }}))])]), __jacJsx("div", {}, [__jacJsx("span", {"className": "mb-2 block text-xs text-muted-foreground"}, ["Stroke width"]), __jacJsx("div", {"className": "flex gap-1.5"}, [width_btn({w: 1, active: (is_conn && (selected["strokeWidth"] === 1)), onClick: _e => {
    if (onStyle) {
      onStyle({"strokeWidth": 1});
    }
  }}), width_btn({w: 2, active: (is_conn && (selected["strokeWidth"] === 2)), onClick: _e => {
    if (onStyle) {
      onStyle({"strokeWidth": 2});
    }
  }}), width_btn({w: 3, active: (is_conn && (selected["strokeWidth"] === 3)), onClick: _e => {
    if (onStyle) {
      onStyle({"strokeWidth": 3});
    }
  }}), width_btn({w: 4, active: (is_conn && (selected["strokeWidth"] === 4)), onClick: _e => {
    if (onStyle) {
      onStyle({"strokeWidth": 4});
    }
  }})])]), __jacJsx("div", {}, [__jacJsx("span", {"className": "mb-2 flex items-center justify-between text-xs text-muted-foreground"}, [__jacJsx("span", {}, ["Opacity"]), __jacJsx("span", {}, [(String(sel_opacity_pct) + "%")])]), __jacJsx("input", {"type": "range", "min": "10", "max": "100", "step": "10", "value": String(sel_opacity_pct), "onChange": e => {
    if (onStyle) {
      onStyle({"opacity": (Math.trunc(Number(e.target.value)) / 100)});
    }
  }, "className": "w-full accent-primary"}, [])]), __jacJsx("div", {}, [__jacJsx("span", {"className": "mb-2 block text-xs text-muted-foreground"}, ["Label"]), __jacJsx("input", {"type": "text", "value": ((is_conn && selected["label"]) ? selected["label"] : ""), "onChange": e => {
    if (onStyle) {
      onStyle({"label": e.target.value});
    }
  }, "placeholder": "e.g. yes · no · 1..*", "className": "h-8 w-full rounded-lg border border-input bg-background px-2.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"}, [])]), __jacJsx("p", {"className": "rounded-lg bg-muted px-2.5 py-2 text-xs text-muted-foreground"}, ["Drag the endpoints to reconnect. Drag anywhere on the line to bend it; double-click a bend to remove it."])]) : null), (((activeTab === "properties") && !has_sel) ? __jacJsx("div", {"className": "flex flex-col gap-5"}, [__jacJsx("div", {}, [section({title: "Page"}), __jacJsx("input", {"type": "text", "value": pageName, "onChange": e => {
    if (onRenamePage) {
      onRenamePage(e.target.value);
    }
  }, "className": "h-8 w-full rounded-lg border border-input bg-background px-2.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"}, [])]), __jacJsx("div", {}, [__jacJsx("span", {"className": "mb-2 block text-xs text-muted-foreground"}, ["Background"]), __jacJsx("div", {"className": "flex gap-1.5"}, [__jacJsx("button", {"className": "size-6 rounded-md border border-border bg-background", "title": "Default"}, []), __jacJsx("button", {"className": "size-6 rounded-md border border-border", "style": {"backgroundColor": "#ffffff"}, "title": "White"}, []), __jacJsx("button", {"className": "size-6 rounded-md border border-border", "style": {"backgroundColor": "#eef1f5"}, "title": "Slate"}, []), __jacJsx("button", {"className": "size-6 rounded-md border border-border", "style": {"backgroundColor": "#1e1e22"}, "title": "Ink"}, [])])]), __jacJsx("div", {"className": "flex items-center justify-between"}, [__jacJsx("span", {"className": "text-sm"}, ["Grid"]), __jacJsx("button", {"onClick": _e => {
    if (onToggleGrid) {
      onToggleGrid(null);
    }
  }, "className": track}, [__jacJsx("span", {"className": knob}, [])])]), __jacJsx("div", {"className": "flex items-center justify-between"}, [__jacJsx("span", {"className": "text-sm"}, ["Appearance"]), __jacJsx("div", {"className": "flex gap-0.5 rounded-lg border border-border p-0.5"}, [__jacJsx("button", {"title": "Light", "onClick": _e => {
    if (onSetTheme) {
      onSetTheme("light");
    }
  }, "className": seg_l}, [__jacJsx(Sun, {"size": 14}, [])]), __jacJsx("button", {"title": "Dark", "onClick": _e => {
    if (onSetTheme) {
      onSetTheme("dark");
    }
  }, "className": seg_d}, [__jacJsx(Moon, {"size": 14}, [])])])]), __jacJsx("div", {}, [section({title: "Sharing"}), __jacJsx("div", {"className": "flex items-center justify-between rounded-lg border border-border px-2.5 py-2"}, [__jacJsx("span", {"className": "flex items-center gap-1.5 text-sm text-muted-foreground"}, [__jacJsx("span", {"className": cn("size-1.5 rounded-full", (pageShared ? "bg-primary" : "bg-success"))}, []), " ", pageAccessLabel]), __jacJsx("button", {"onClick": onSharePage, "className": "flex items-center gap-1 text-xs font-medium text-primary hover:underline"}, [__jacJsx(Share2, {"size": 13}, []), " ", (pageShared ? "Manage" : "Share diagram")])])]), __jacJsx("button", {"onClick": onExport, "className": "flex items-center justify-center gap-2 rounded-lg border border-border py-2 text-sm hover:bg-accent"}, [__jacJsx(Download, {"size": 15}, []), " Export page (PNG)"])]) : null), ((activeTab === "layers") ? ((elements.length > 0) ? __jacJsx("div", {"className": "flex flex-col gap-0.5"}, [layer_rows().map(r => render_row({r: r}))]) : __jacJsx("div", {"className": "flex h-full flex-col items-center justify-center gap-2 py-16 text-center"}, [__jacJsx("div", {"className": "flex size-10 items-center justify-center rounded-full bg-muted text-muted-foreground"}, [__jacJsx(Layers, {"size": 18}, [])]), __jacJsx("p", {"className": "text-sm font-medium"}, ["No objects yet"]), __jacJsx("p", {"className": "text-xs text-muted-foreground"}, ["Shapes you add to this page appear here, in z-order."])])) : null), ((activeTab === "comments") ? __jacJsx("div", {"className": "flex flex-col gap-3"}, [__jacJsx("div", {"className": "flex items-center justify-between"}, [__jacJsx("div", {"className": "flex gap-1.5"}, [comment_chip({f: "all", label: "All"}), comment_chip({f: "open", label: "Open"}), comment_chip({f: "resolved", label: "Resolved"}), comment_chip({f: "mine", label: "Mine"})]), __jacJsx("button", {"title": "New comment (click canvas)", "onClick": onStartComment, "className": "flex size-7 items-center justify-center rounded-md text-primary hover:bg-primary-soft"}, [__jacJsx(Plus, {"size": 16}, [])])]), ((comments.filter(c => comment_matches(c, commentFilter)).map(c => c).length === 0) ? __jacJsx("div", {"className": "flex flex-col items-center justify-center gap-2 py-14 text-center"}, [__jacJsx("div", {"className": "flex size-10 items-center justify-center rounded-full bg-muted text-muted-foreground"}, [__jacJsx(MessageSquare, {"size": 18}, [])]), __jacJsx("p", {"className": "text-sm font-medium"}, ["No comments"]), __jacJsx("p", {"className": "text-xs text-muted-foreground"}, ["Pick the comment tool and click the canvas to start a thread."])]) : __jacJsx("div", {"className": "flex flex-col gap-2"}, [_jac.builtin.range(comments.length).filter(i => comment_matches(_jac.poly.getitem(comments, i), commentFilter)).map(i => comment_row({c: _jac.poly.getitem(comments, i), idx: i}))]))]) : null), ((activeTab === "ai") ? __jacJsx("div", {"className": "flex flex-col gap-4"}, [__jacJsx("div", {"className": "rounded-lg bg-primary-soft px-3 py-2 text-xs text-muted-foreground"}, ["AI diagram generation is coming soon."]), __jacJsx("div", {"className": "rounded-xl border border-border bg-background p-2.5 opacity-60"}, [__jacJsx("textarea", {"disabled": true, "placeholder": "Ask LibreDraw AI... e.g. 'Cloud architecture for a SaaS app'", "className": "h-20 w-full resize-none bg-transparent text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed"}, []), __jacJsx("div", {"className": "flex flex-wrap items-center gap-2 border-t border-border pt-2"}, [__jacJsx("select", {"disabled": true, "className": "min-w-0 flex-1 rounded-md border border-border bg-card px-2 py-1 text-xs text-muted-foreground outline-none disabled:cursor-not-allowed"}, [__jacJsx("option", {}, ["Current page"]), __jacJsx("option", {}, ["Selection"]), __jacJsx("option", {}, ["Entire document"])]), __jacJsx("button", {"disabled": true, "title": "Coming soon", "className": "shrink-0 flex items-center gap-1.5 rounded-lg bg-primary px-2.5 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed"}, [__jacJsx(Send, {"size": 13}, []), " Generate"])])]), __jacJsx("div", {}, [section({title: "Quick actions"}), __jacJsx("div", {"className": "flex flex-col gap-1.5"}, [__jacJsx("button", {"disabled": true, "title": "Coming soon", "className": "flex items-center gap-2 rounded-lg border border-border px-2.5 py-2 text-sm hover:border-primary hover:bg-primary-soft disabled:opacity-50 disabled:cursor-not-allowed"}, ["Improve layout"]), __jacJsx("button", {"disabled": true, "title": "Coming soon", "className": "flex items-center gap-2 rounded-lg border border-border px-2.5 py-2 text-sm hover:border-primary hover:bg-primary-soft disabled:opacity-50 disabled:cursor-not-allowed"}, ["Convert to Mermaid"]), __jacJsx("button", {"disabled": true, "title": "Coming soon", "className": "flex items-center gap-2 rounded-lg border border-border px-2.5 py-2 text-sm hover:border-primary hover:bg-primary-soft disabled:opacity-50 disabled:cursor-not-allowed"}, ["Explain this diagram"]), __jacJsx("button", {"disabled": true, "title": "Coming soon", "className": "flex items-center gap-2 rounded-lg border border-border px-2.5 py-2 text-sm hover:border-primary hover:bg-primary-soft disabled:opacity-50 disabled:cursor-not-allowed"}, ["Generate from sticky notes"])])])]) : null)])]);
}
/*jac:refresh-boundary*/;
export {RightPanel};
//# sourceMappingURL=RightPanel.js.map
